import { __awaiter, __decorate, __param, __metadata } from 'tslib';
import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, Input, Pipe, ViewChild, Output, ViewContainerRef, LOCALE_ID, Inject, ViewEncapsulation, DEFAULT_CURRENCY_CODE, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { Subscription, BehaviorSubject } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import * as i1 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as i1$1 from '@angular/common/http';
import * as i2 from '@bento/bento-ng';
import { BentoModule } from '@bento/bento-ng';
import * as i3 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { FileDrawer, isClientDrawer, ItemType, RootFolderType, ContainerKey } from '@onviobr/toolbox-document-services/dist/esm5/models';
import { hasSome } from '@onviobr/services/permission';
import { getAccounts } from '@onviobr/services/profile/user';
import * as i1$2 from '@onviobr/toolbox-document-services/dist/esm5';
import * as i3$1 from '@onviobr/document-components';
import { DocumentComponentsModule } from '@onviobr/document-components';
import * as i7 from '@angular/common';
import { formatCurrency, getLocaleCurrencySymbol, getLocaleCurrencyCode, registerLocaleData, CommonModule } from '@angular/common';
import { IconType } from '@onviobr/ui-elements/bm-icons';
import moment from 'moment';
import * as i1$3 from '@ng-bootstrap/ng-bootstrap';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import * as wjcGrid from '@grapecity/wijmo.grid';
import * as wijmo from '@grapecity/wijmo';
import { CollectionView } from '@grapecity/wijmo';
import * as i5 from '@grapecity/wijmo.angular2.grid';
import { WjGridModule } from '@grapecity/wijmo.angular2.grid';
import { saveAs } from 'file-saver';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { getCompanyId, getContactId } from '@onviobr/services/session';
import { cookie, jsonHeaders, throwOn } from '@onviobr/services/utils';
import localEnGb from '@angular/common/locales/en-GB';
import localFrCa from '@angular/common/locales/fr-CA';
import { getRoute } from '@onviobr/services/environment';
import { getCountryCode } from '@onviobr/services/environment/language';

var ImportSource;
(function (ImportSource) {
    ImportSource["Computer"] = "MY_COMPUTER";
    ImportSource["Documents"] = "DOCUMENTS_IN_ONVIO";
})(ImportSource || (ImportSource = {}));

/**
 * Observable Data Service
 */
class SelectFileService {
    constructor() {
        this.subscription = new Subscription();
        this.selectedFilesSubject = new BehaviorSubject([]);
        this.isFileSelectedSubject = new BehaviorSubject(false);
        this.importSourceSubject = new BehaviorSubject(ImportSource.Computer);
        this.selectedFileMetadataSubject = new BehaviorSubject(null);
        // Clear selected files collection if import source was changed (ex. from Computer to Documents).
        this.subscription.add(this.importSourceObservable.subscribe(importSource => {
            if (this.previousImportSource !== importSource) {
                this.selectedFilesSubject.next([]);
                this.selectedFileMetadataSubject.next(null);
            }
            this.previousImportSource = importSource;
        }));
        // Update file(s) selected state if selected files collection was modified.
        this.subscription.add(this.selectedFilesSubject.subscribe(files => {
            if (this.importSource === ImportSource.Computer) {
                const isFileSelected = files && files.length > 0;
                this.isFileSelectedSubject.next(isFileSelected);
            }
        }));
        this.subscription.add(this.selectedFileMetadataSubject.subscribe(fileMetadata => {
            if (this.importSource === ImportSource.Documents) {
                const isFileSelected = fileMetadata !== null;
                this.isFileSelectedSubject.next(isFileSelected);
            }
        }));
    }
    get selectedFiles() {
        return this.selectedFilesSubject.value;
    }
    get isFileSelected() {
        return this.isFileSelectedSubject.value;
    }
    get isFileSelectedObservable() {
        return this.isFileSelectedSubject.asObservable();
    }
    get importSource() {
        return this.importSourceSubject.value;
    }
    set importSource(value) {
        this.importSourceSubject.next(value);
    }
    get importSourceObservable() {
        return this.importSourceSubject.asObservable();
    }
    get selectedFileMetadata() {
        return this.selectedFileMetadataSubject.value;
    }
    get selectedFileMetadataObservable() {
        return this.selectedFileMetadataSubject.asObservable();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /**
     * Updates file selection according to user input
     * @param selectedFiles - Files selected by user
     */
    selectFilesFromDrive(selectedFiles) {
        this.selectedFilesSubject.next(selectedFiles ? selectedFiles : []);
    }
    /**
     * Updates file selection from DMS according to user input
     * @param selectedFile - File selected by user
     */
    selectFileFromDocuments(selectedFile) {
        this.selectedFileMetadataSubject.next(selectedFile);
    }
}
SelectFileService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectFileService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SelectFileService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectFileService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectFileService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

/**
 * Observable Data Service
 */
class ImportContextService {
    constructor() {
        this.subscription = new Subscription();
        this.sheetsNamesSubject = new BehaviorSubject([]);
        this.selectedSheetSubject = new BehaviorSubject('');
        this.sheetDataSubject = new BehaviorSubject([]);
        this.columnsMappingsSubject = new BehaviorSubject(new Map());
        this.skipFirstRowSubject = new BehaviorSubject(true);
        this.subscription.add(this.sheetsNamesObservable.subscribe(sheetsNames => {
            // Sets the default sheet name
            this.selectedSheetSubject.next(sheetsNames[0]);
        }));
    }
    get sheetData() {
        return this.sheetDataSubject.value;
    }
    set sheetData(value) {
        this.sheetDataSubject.next(value);
    }
    get selectedSheet() {
        return this.selectedSheetSubject.value;
    }
    set selectedSheet(value) {
        this.selectedSheetSubject.next(value);
    }
    get sheetsNamesObservable() {
        return this.sheetsNamesSubject.asObservable();
    }
    set sheetsNames(value) {
        this.sheetsNamesSubject.next(value);
    }
    get skipFirstRow() {
        return this.skipFirstRowSubject.value;
    }
    set skipFirstRow(value) {
        this.skipFirstRowSubject.next(value);
    }
    get selectedSheetObservable() {
        return this.selectedSheetSubject.asObservable();
    }
    get sheetDataObservable() {
        return this.sheetDataSubject.asObservable();
    }
    get columnsMappingsObservable() {
        return this.columnsMappingsSubject.asObservable();
    }
    get columnsMappings() {
        return this.columnsMappingsSubject.value;
    }
    set columnsMappings(value) {
        this.columnsMappingsSubject.next(value);
    }
    get skipFirstRowObservable() {
        return this.skipFirstRowSubject.asObservable();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /**
     * Gets number of rows of data excluding the header
     */
    getNumRows() {
        const sheetData = this.sheetData;
        const skipFirstRow = this.skipFirstRow;
        return skipFirstRow ? sheetData.length - 1 : sheetData.length;
    }
}
ImportContextService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportContextService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ImportContextService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportContextService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportContextService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

/**
 * This file was copied from onbalance-excel-importer
 * https://dev.azure.com/tr-tax/TaxProf/_git/aafm-onbalance-excel-importer
 */
class ExcelParserJsonService {
    excelParseJsonSheet(sheet) {
        // If there is no !ref prop, the sheet is empty
        if (!sheet['!ref']) {
            return [];
        }
        // Transform a XLSX sheet data structure to a matrix
        const matrix = this.createRangeMatrix(sheet['!ref']);
        for (const cell in sheet) {
            // eslint-disable-next-line @typescript-eslint/prefer-string-starts-ends-with
            if (cell[0] !== '!') {
                const coor = this.getCellCoordinates(cell);
                matrix[coor[1]][coor[0]] = this.excelValueParse(sheet[cell]);
            }
        }
        return matrix;
    }
    excelValueParse(value) {
        // Parse the values based on the type that Excel says it is
        switch (value.t) {
            case 'n':
                if (typeof value.v === 'number' && typeof value.z === 'string' && XLSX.SSF.is_date(value.z)) {
                    const midDate = XLSX.SSF.parse_date_code(value.v);
                    return new Date(midDate.y, midDate.m - 1, midDate.d);
                }
                return Number(value.v);
            default:
                return value.v;
        }
    }
    createRangeMatrix(range) {
        // Creates a matrix based on a Excel range
        const coors = range.split(':').map(coor => this.getCellCoordinates(coor));
        // Get column and row counts, default to one if the range is a single cell
        const columns = coors.length === 2 ? coors[1][0] - coors[0][0] + 1 : 1;
        const rows = coors.length === 2 ? coors[1][1] - coors[0][1] + 1 : 1;
        // Fill the matrix (rows by columns) with nulls
        return this.nulledArray(rows, () => this.nulledArray(columns));
    }
    getCellCoordinates(cellName) {
        // Divides the cell name with a regex to get the row part and column part.
        const parts = /([A-z]*)([0-9]*)/.exec(cellName);
        const letters = parts[1].toUpperCase().split('');
        let letterIndex = 0;
        // Iterates over each letter, get the charcode minus the 'a' charcode to get a zero-based index.
        // Plus 'i' * 25 to get the index when there is more than one letter, like AB23.
        // The plus 'i' is a fix in the number sequence, will be removed later.
        for (let i = 0; i < letters.length; i++) {
            letterIndex = letters[i].charCodeAt(0) - 65 + i * 26;
        }
        return [letterIndex, Number(parts[2]) - 1];
    }
    nulledArray(length, fn) {
        // Creates an array with a defined length and filled with the return of fn() or a null
        return Array.apply(null, Array(length)).map(() => (fn ? fn() : null));
    }
}
ExcelParserJsonService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ExcelParserJsonService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ExcelParserJsonService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ExcelParserJsonService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ExcelParserJsonService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class AlertService {
    constructor(translateService) {
        this.translateService = translateService;
        this.onAlertAdded = new EventEmitter();
        this.onAllDismissed = new EventEmitter();
    }
    addAlert(type, message, timeout = null) {
        const option = {
            type,
            msg: this.translateService.instant(message.key, message.params),
            timeout,
            closeable: true,
        };
        this.onAlertAdded.emit(option);
    }
    dismissAll() {
        this.onAllDismissed.emit();
    }
}
AlertService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AlertService, deps: [{ token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
AlertService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AlertService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AlertService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.TranslateService }]; } });

class FileDataService {
    constructor(importContextService, selectFileService, excelParserService, alertService) {
        this.importContextService = importContextService;
        this.selectFileService = selectFileService;
        this.excelParserService = excelParserService;
        this.alertService = alertService;
        this.sheetsData = new Map();
        this.subscription = new Subscription();
        this.subscription.add(selectFileService.isFileSelectedObservable.subscribe(() => {
            this.sheetsData.clear();
            this.fileContent = '';
            this.isFileParsedSuccessfully = true;
            this.importContextService.sheetData = null;
            this.importContextService.columnsMappings = new Map();
            this.importContextService.skipFirstRow = true;
        }));
        this.subscription.add(importContextService.selectedSheetObservable.subscribe(selectedSheet => {
            this.importContextService.sheetData = this.getData(selectedSheet);
        }));
    }
    get isFileContentLoaded() {
        return this.fileContent !== '';
    }
    get isFileParsed() {
        return this.isFileParsedSuccessfully;
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /**
     * Returns sheet data from the cache
     * @param sheetName Selected sheet name
     */
    getData(sheetName) {
        if (this.sheetsData.has(sheetName)) {
            return this.sheetsData.get(sheetName);
        }
        const rawSheetData = this.getSheetData(this.fileContent, sheetName);
        const sheetData = this.skipBlankRows(rawSheetData);
        this.sheetsData.set(sheetName, sheetData);
        return sheetData;
    }
    /**
     * Removes blank lines from the output
     * @param rawSheetData sheet data
     */
    skipBlankRows(rawSheetData) {
        return rawSheetData ? rawSheetData.filter(row => row.some(it => it !== null)) : null;
    }
    /**
     * Returns the sheet names for the selected file
     * @param fileContent - Uploaded file
     */
    getSheetsNames(fileContent) {
        // Specify the data to parse from the file
        const parsingOptions = {};
        parsingOptions.type = 'binary';
        parsingOptions.bookSheets = true;
        const sheetData = this.readXlsxDataFromFile(fileContent, parsingOptions);
        return sheetData === null ? null : sheetData.SheetNames;
    }
    /**
     * Returns the sheet data
     * @param sheetName - Selected sheet name
     */
    getSheetData(fileContent, sheetName) {
        if (fileContent === '') {
            return null;
        }
        // Specify the data to parse from the file
        const parsingOptions = {};
        parsingOptions.type = 'binary';
        parsingOptions.cellNF = true;
        const workbook = this.readXlsxDataFromFile(fileContent, parsingOptions);
        return (workbook === null || workbook === void 0 ? void 0 : workbook.Sheets[sheetName]) ? this.excelParserService.excelParseJsonSheet(workbook.Sheets[sheetName]) : null;
    }
    /**
     * Loads initial data (file content)
     */
    loadInitialDataAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isFileContentLoaded && this.isFileParsedSuccessfully) {
                return;
            }
            if (!this.selectFileService.isFileSelected) {
                throw new Error('The file is not selected');
            }
            this.fileContent = yield this.loadFile(this.selectFileService.selectedFiles[0]);
            const sheetsNames = this.getSheetsNames(this.fileContent);
            if (sheetsNames) {
                this.importContextService.sheetsNames = sheetsNames;
            }
        });
    }
    /**
     * Read XLSX data from file content
     * @param fileContent - file content
     * @param parsingOptions - parsing options
     */
    readXlsxDataFromFile(fileContent, parsingOptions) {
        try {
            const workBook = XLSX.read(fileContent, parsingOptions);
            this.isFileParsedSuccessfully = true;
            return workBook;
        }
        catch (error) {
            this.isFileParsedSuccessfully = false;
            this.alertService.addAlert('danger', {
                key: 'bm.import.wizard.validation.processingError',
            });
            return null;
        }
    }
    /**
     * Uploads selected file
     * @param file - Selected file
     */
    loadFile(file) {
        return __awaiter(this, void 0, void 0, function* () {
            const promise = new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = e => {
                    let dataString = '';
                    if (reader.result instanceof ArrayBuffer) {
                        const buffer = new Uint8Array(reader.result);
                        for (const char of buffer) {
                            dataString += String.fromCharCode(char);
                        }
                    }
                    resolve(dataString);
                };
                reader.readAsArrayBuffer(file);
            });
            return promise;
        });
    }
}
FileDataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FileDataService, deps: [{ token: ImportContextService }, { token: SelectFileService }, { token: ExcelParserJsonService }, { token: AlertService }], target: i0.ɵɵFactoryTarget.Injectable });
FileDataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FileDataService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FileDataService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ImportContextService }, { type: SelectFileService }, { type: ExcelParserJsonService }, { type: AlertService }]; } });

class ValidatorBuilder {
    addValidator(nextValidator) {
        if (this.firstValidator) {
            this.lastValidator.setNextValidator(nextValidator);
            this.lastValidator = nextValidator;
        }
        else {
            this.firstValidator = nextValidator;
            this.lastValidator = this.firstValidator;
        }
    }
    build() {
        return this.firstValidator;
    }
}

class ValueValidatorBuilder extends ValidatorBuilder {
}

function convertStringToNumber(value) {
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'string') {
        return Number(value);
    }
    return NaN;
}
function isValueInRange(value, minValue, maxValue) {
    if (Number.isNaN(value) || (minValue !== null && value < minValue) || (maxValue !== null && value > maxValue)) {
        return false;
    }
    return true;
}
function isEmpty(value) {
    if (value === null || value === undefined) {
        return true;
    }
    const valueStr = value.toString();
    if (valueStr.trim().length === 0) {
        return true;
    }
    return false;
}
function countDecimals(value) {
    if (!value || Math.floor(value) === value) {
        return 0;
    }
    return value.toString().split('.')[1].length || 0;
}

class ErrorsMerger {
    mergeFileErrors(currentResult, nextResult) {
        for (const [rowIndex, validationRowErrors] of nextResult) {
            if (currentResult.has(rowIndex)) {
                currentResult.set(rowIndex, this.mergeRowErrors(currentResult.get(rowIndex), validationRowErrors));
            }
            else {
                currentResult.set(rowIndex, validationRowErrors);
            }
        }
        return currentResult;
    }
    mergeRowErrors(currentResult, nextResult) {
        for (const [columnIndex, validationError] of nextResult) {
            if (currentResult.has(columnIndex)) {
                currentResult.set(columnIndex, this.mergeValueErrors(currentResult.get(columnIndex), validationError));
            }
            else {
                currentResult.set(columnIndex, validationError);
            }
        }
        return currentResult;
    }
    mergeValueErrors(currentResult, nextResult) {
        if (currentResult) {
            if (nextResult) {
                currentResult.error.push(...nextResult.error);
            }
            return currentResult;
        }
        return nextResult;
    }
}

class Validator {
    constructor(proceedOnError) {
        this.proceedOnError = proceedOnError;
        this.errorsMerger = new ErrorsMerger();
    }
    validate(value) {
        const validationResult = this.validateSpecifically(value);
        if (validationResult) {
            if (this.proceedOnError && this.nextValidator) {
                const nextValidationResult = this.nextValidator.validate(value);
                if (nextValidationResult) {
                    return this.mergeErrors(validationResult, nextValidationResult);
                }
            }
            return validationResult;
        }
        if (this.nextValidator) {
            return this.nextValidator.validate(value);
        }
        return null;
    }
    setNextValidator(validator) {
        this.nextValidator = validator;
    }
}

class ValueValidator extends Validator {
    constructor(error, howToFix, proceedOnError) {
        super(proceedOnError);
        this.error = error;
        this.howToFix = howToFix;
    }
    validateSpecifically(value) {
        if (this.isValid(value)) {
            return null;
        }
        return this.buildError();
    }
    buildError() {
        return {
            error: [this.error],
            howToFix: [this.howToFix],
        };
    }
    mergeErrors(currentResult, nextResult) {
        return this.errorsMerger.mergeValueErrors(currentResult, nextResult);
    }
}

class RequiredValueValidator extends ValueValidator {
    constructor(error, howToFix) {
        super(error, howToFix, false);
    }
    isValid(value) {
        return !isEmpty(value);
    }
}

function defaultColumnMatcher(columnHeader, sheetHeader) {
    return clean(columnHeader) === clean(sheetHeader);
}
function clean(s) {
    return s
        .trim() // Remove leading/trailing space
        .replace(/\s+/g, ' ') // Remove extra spaces in between words
        .toLowerCase();
}
function synonymColumnMatcher(sheetHeader, synonyms) {
    return synonyms.some(h => defaultColumnMatcher(h, sheetHeader));
}
/**
 * Checks whether sheetHeader, representing the header of a column in a given xls doc, matches an available column given certain constraints
 */
class ColumnMatcher {
    constructor(options = {}) {
        this.synonyms = [...(options.synonyms || [])];
        this.columnHeader = options.columnHeader;
        this.translateService = options.translateService;
    }
    matches(sheetHeader, colHeader) {
        const columnHeader = colHeader || this.columnHeader;
        let synonyms = columnHeader ? this.synonyms.concat(columnHeader) : this.synonyms;
        if (this.translateService) {
            sheetHeader = this.translateService.instant(sheetHeader);
            synonyms = synonyms.map(s => this.translateService.instant(s));
        }
        return synonymColumnMatcher(sheetHeader, synonyms);
    }
}
class ColumnOptions {
    constructor(options) {
        this.name = options.name;
        this.text = options.translateService.instant(options.textLocalizationKey);
        this.isMandatory = options.isMandatory;
        this.validatorBuilder = new ValueValidatorBuilder();
        this.displayOptions = options.displayOptions;
        if (options.isMandatory) {
            if (!options.isMandatoryError) {
                options.isMandatoryError = options.translateService.instant('bm.import.wizard.validation.mandatoryError', {
                    columnText: this.text,
                });
            }
            const mandatoryErrorHowToFix = options.translateService.instant('bm.import.wizard.validation.mandatoryErrorHowToFix', { columnText: this.text });
            this.validatorBuilder.addValidator(new RequiredValueValidator(options.isMandatoryError, mandatoryErrorHowToFix));
        }
        this.columnMatcher = options.matcher || new ColumnMatcher({ translateService: options.translateService });
    }
    buildValidator() {
        return this.validatorBuilder.build();
    }
    /**
     * Checks whether this column matches a sheet's column's header
     * Used to automatically mapped sheet headers with available columns
     * @param sheetHeader header in a given sheet's column
     */
    matches(sheetHeader) {
        return this.columnMatcher.matches(sheetHeader, this.text);
    }
}

class ColumnMappingService {
    constructor(importContextService, translateService) {
        this.importContextService = importContextService;
        this.translateService = translateService;
        this.subscription = new Subscription();
        // Storage for the column mappings
        this.columnsMappings = new Map();
        // Storage for all available types
        this.availableColumnMappings = [];
        this.isMandatoryColumnsMappedSubject = new BehaviorSubject(false);
        this.subscription.add(importContextService.sheetDataObservable.subscribe(() => {
            this.reset();
        }));
    }
    get isMandatoryColumnsMappedObservable() {
        return this.isMandatoryColumnsMappedSubject.asObservable();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /**
     * Gets the mapping for the given index
     * @param columnIndex The column index
     */
    getColumnMapping(columnIndex) {
        if (this.columnsMappings.has(columnIndex)) {
            return this.columnsMappings.get(columnIndex);
        }
        return this.getNotUsedMappingOptions();
    }
    /**
     * Gets all available mapping options
     * @param columnIndex Column for which available options are being taken
     */
    getAvailableColumnMappings(columnIndex = -1) {
        const availableColumnMappings = this.getFilteredColumnMappingOptions(columnIndex);
        // NotUsed option should be added by Default
        availableColumnMappings.unshift(this.getNotUsedMappingOptions());
        return availableColumnMappings;
    }
    /**
     * Sets a column mapping
     * @param columnIndex The column index
     * @param columnOptions Column options
     */
    setColumnMapping(columnIndex, columnOptions) {
        if (columnOptions.name === 'NotUsed') {
            // Deletes notUsed mappings
            this.columnsMappings.delete(columnIndex);
        }
        else {
            this.columnsMappings.set(columnIndex, columnOptions);
        }
        this.importContextService.columnsMappings = this.columnsMappings;
        this.isMandatoryColumnsMappedSubject.next(this.isMandatoryColumnsMapped());
    }
    /**
     * Initialize available column mappings
     * @param mappingColumnOptions The available columns for specific import type
     */
    initializeColumnMappings(mappingColumnOptions, automapHeaders) {
        this.availableColumnMappings = mappingColumnOptions;
        if (automapHeaders) {
            this.setAutomappedColumns(automapHeaders);
        }
    }
    setAutomappedColumns(headerRow) {
        for (let i = 0; i < headerRow.length; ++i) {
            const header = headerRow[i];
            const availableColumns = this.getAvailableColumnMappings(i);
            const column = availableColumns.find(c => c.matches(header));
            if (column) {
                this.setColumnMapping(i, column);
            }
        }
    }
    /**
     * Gets default mapping options
     */
    getNotUsedMappingOptions() {
        return new ColumnOptions({
            translateService: this.translateService,
            name: 'NotUsed',
            textLocalizationKey: 'bm.import.wizard.common.notUsed',
            isMandatory: false,
        });
    }
    /**
     * Gets filtered and sorted mapping options
     * @param columnIndex Column for which available options are being taken
     */
    getFilteredColumnMappingOptions(columnIndex) {
        const currentColumn = this.getColumnMapping(columnIndex);
        return this.availableColumnMappings
            .filter(el => ![...this.columnsMappings.values()].map(e => e.name).includes(el.name) || el.name === currentColumn.name)
            .sort((x, y) => {
            // Sort Mandatory columns first
            const xM = x.isMandatory ? 0 : 1;
            const yM = y.isMandatory ? 0 : 1;
            return xM - yM;
        });
    }
    /**
     * Returns true is all mandatory columns mapped
     */
    isMandatoryColumnsMapped() {
        return (this.columnsMappings.size > 0 &&
            !this.getAvailableColumnMappings()
                .map(e => e.isMandatory)
                .includes(true));
    }
    /**
     * Resets the column mappings
     */
    reset() {
        this.columnsMappings.clear();
        this.isMandatoryColumnsMappedSubject.next(false);
    }
}
ColumnMappingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ColumnMappingService, deps: [{ token: ImportContextService }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
ColumnMappingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ColumnMappingService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ColumnMappingService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ImportContextService }, { type: i1.TranslateService }]; } });

class FileValidator extends Validator {
    mergeErrors(currentResult, nextResult) {
        return this.errorsMerger.mergeFileErrors(currentResult, nextResult);
    }
}

class DefaultFileValidator extends FileValidator {
    constructor(rowValidator, skipFirstRow) {
        super(true);
        this.rowValidator = rowValidator;
        this.firstRowIndex = skipFirstRow ? 1 : 0;
    }
    validateSpecifically(file) {
        const fileErrors = new Map();
        for (let rowIndex = this.firstRowIndex; rowIndex < file.length; rowIndex++) {
            const row = file[rowIndex];
            // ignore empty rows
            if (row.some(it => it !== null)) {
                const rowErrors = this.rowValidator.validate(row);
                if (rowErrors) {
                    fileErrors.set(rowIndex, rowErrors);
                }
            }
        }
        if (fileErrors.size > 0) {
            return fileErrors;
        }
        return null;
    }
}

class RowValidator extends Validator {
    mergeErrors(currentResult, nextResult) {
        return this.errorsMerger.mergeRowErrors(currentResult, nextResult);
    }
    addValidationError(columnIndex, error, howToFix, validationResult) {
        const validationError = validationResult.get(columnIndex);
        if (validationError) {
            validationError.error.push(error);
            validationError.howToFix.push(howToFix);
        }
        else {
            validationResult.set(columnIndex, { error: [error], howToFix: [howToFix] });
        }
    }
}

class DefaultRowValidator extends RowValidator {
    constructor(valueValidators) {
        super(true);
        this.valueValidators = valueValidators;
    }
    validateSpecifically(row) {
        const errors = new Map();
        for (const [columnIndex, validator] of this.valueValidators) {
            const error = validator.validate(row[columnIndex]);
            if (error) {
                errors.set(columnIndex, error);
            }
        }
        if (errors.size === 0) {
            return null;
        }
        return errors;
    }
}

class RowValidatorBuilder extends ValidatorBuilder {
    constructor(mappingOptions, mappingColumnsOptions) {
        super();
        const valueValidators = new Map();
        for (const [columnIndex, mappingOption] of mappingColumnsOptions) {
            const validator = mappingOption.buildValidator();
            if (validator) {
                valueValidators.set(columnIndex, validator);
            }
        }
        if (valueValidators.size > 0) {
            this.addValidator(new DefaultRowValidator(valueValidators));
        }
        if (mappingOptions.getRowValidators) {
            const rowValidators = mappingOptions.getRowValidators(mappingColumnsOptions);
            for (const rowValidator of rowValidators) {
                this.addValidator(rowValidator);
            }
        }
    }
}

class FileValidatorBuilder extends ValidatorBuilder {
    constructor(mappingOption, mappingColumnsOptions, skipFirstRow) {
        super();
        const rowValidator = new RowValidatorBuilder(mappingOption, mappingColumnsOptions).build();
        if (rowValidator) {
            this.addValidator(new DefaultFileValidator(rowValidator, skipFirstRow));
        }
        if (mappingOption.getFileValidators) {
            const fileValidators = mappingOption.getFileValidators(mappingColumnsOptions);
            for (const fileValidator of fileValidators) {
                this.addValidator(fileValidator);
            }
        }
    }
}

class ValidationService {
    constructor(importContextService) {
        this.importContextService = importContextService;
        this.subscription = new Subscription();
        this.isFileValidSubject = new BehaviorSubject(null);
        this.subscription.add(this.importContextService.sheetDataObservable.subscribe((data) => {
            this.validationErrors = null;
        }));
        this.subscription.add(this.importContextService.skipFirstRowObservable.subscribe((skipFirstRow) => {
            this.validationErrors = null;
        }));
        this.subscription.add(this.importContextService.selectedSheetObservable.subscribe((name) => {
            this.validationErrors = null;
        }));
        this.subscription.add(this.importContextService.columnsMappingsObservable.subscribe((name) => {
            this.validationErrors = null;
        }));
    }
    get validationErrors() {
        return this.errors;
    }
    set validationErrors(value) {
        this.errors = value;
        if (value === null) {
            this.isFileValid = null;
        }
        else {
            this.isFileValid = value.size === 0;
        }
    }
    get isFileValidObservable() {
        return this.isFileValidSubject.asObservable();
    }
    get isFileValid() {
        return this.isFileValidSubject.value;
    }
    set isFileValid(value) {
        this.isFileValidSubject.next(value);
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    validate(mappingOptions) {
        if (this.isFileValid === null) {
            const fileValidator = new FileValidatorBuilder(mappingOptions, this.importContextService.columnsMappings, this.importContextService.skipFirstRow).build();
            // Can be null if no validation rules provided
            if (fileValidator) {
                const validationResult = fileValidator.validate(this.importContextService.sheetData);
                if (validationResult) {
                    this.validationErrors = validationResult;
                    return;
                }
            }
            this.validationErrors = new Map();
        }
    }
    getValidationGridData() {
        const firstIndex = this.importContextService.skipFirstRow ? 1 : 0;
        // File is valid
        if (this.isFileValid) {
            return this.getFirstNValidRows(this.importContextService.sheetData, firstIndex, 10);
        }
        // File is not valid
        if (this.isFileValid !== null) {
            const first10ErrorIndexes = Array.from(this.validationErrors.keys()).slice(0, 10);
            const validationData = [];
            for (const rowIndex of first10ErrorIndexes) {
                validationData.push(this.mapValidationRow(rowIndex));
            }
            if (validationData.length < 10) {
                validationData.push(...this.getFirstNValidRows(this.importContextService.sheetData, firstIndex, 10 - validationData.length, this.validationErrors));
            }
            return validationData;
        }
        // File is not validated
        return null;
    }
    getFirstNValidRows(file, firstIndex, count, errors = new Map()) {
        const result = [];
        for (let rowIndex = firstIndex; rowIndex < file.length; rowIndex++) {
            if (result.length === count) {
                break;
            }
            if (file[rowIndex].some(it => it !== null) && !errors.has(rowIndex)) {
                result.push(file[rowIndex].map(this.mapValidationValue));
            }
        }
        return result;
    }
    mapValidationRow(rowIndex) {
        const row = this.importContextService.sheetData[rowIndex].map(this.mapValidationValue);
        const errors = this.validationErrors.get(rowIndex);
        for (const [columnIndex, validationError] of errors) {
            row[columnIndex].errorTooltip = validationError.error.join('. ');
            row[columnIndex].hasError = true;
        }
        return row;
    }
    mapValidationValue(value) {
        return { hasError: false, value, errorTooltip: null };
    }
}
ValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ValidationService, deps: [{ token: ImportContextService }], target: i0.ɵɵFactoryTarget.Injectable });
ValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ValidationService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ValidationService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ImportContextService }]; } });

class SelectFileFromDocumentsService {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    getFileData(document) {
        return __awaiter(this, void 0, void 0, function* () {
            const promise = new Promise((resolve, reject) => {
                this.httpClient
                    .get(this.getUrl(document), {
                    responseType: 'blob',
                    headers: {
                        accept: document.mimeType,
                    },
                })
                    .toPromise()
                    .then(blob => {
                    // Success
                    const file = new File([blob], document.name, { type: blob.type });
                    resolve(file);
                }, error => {
                    // Error
                    reject(error);
                });
            });
            return promise;
        });
    }
    getUrl(document) {
        return `/api/storage/v1/folders/${document.parent.id}/documents/${document.id}`;
    }
}
SelectFileFromDocumentsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectFileFromDocumentsService, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
SelectFileFromDocumentsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectFileFromDocumentsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectFileFromDocumentsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }]; } });

class SelectImportSourceComponent {
    constructor(selectFileService, translateService) {
        this.selectFileService = selectFileService;
        this.translateService = translateService;
    }
    ngOnInit() {
        this.comboboxItems = new BehaviorSubject([
            {
                id: ImportSource.Computer,
                text: this.translateService.instant('bm.import.wizard.selectImportSource.option.computer'),
            },
            {
                id: ImportSource.Documents,
                text: this.translateService.instant('bm.import.wizard.selectImportSource.option.documents'),
            },
        ]);
        this.comboboxOptions = {
            labelFormatter: row => row.text,
        };
        this.selectedImportSource = this.comboboxItems.value[0];
        this.onSelectionChanged(this.selectedImportSource);
    }
    onSelectionChanged(importSourceOption) {
        this.selectFileService.importSource = importSourceOption.id;
    }
}
SelectImportSourceComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectImportSourceComponent, deps: [{ token: SelectFileService }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Component });
SelectImportSourceComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: SelectImportSourceComponent, selector: "select-import-source", ngImport: i0, template: "<div class=\"form-group\">\n  <label for=\"basic-combobox\"> {{ 'bm.import.wizard.selectImportSource.label' | translate }} </label>\n  <bento-combobox\n    [(ngModel)]=\"selectedImportSource\"\n    [itemsObservable]=\"comboboxItems\"\n    [options]=\"comboboxOptions\"\n    [rowTemplate]=\"importSourceComboboxTemplate\"\n    [placeholder]=\"false\"\n    (ngModelChange)=\"onSelectionChanged($event)\"\n  >\n  </bento-combobox>\n</div>\n\n<ng-template #importSourceComboboxTemplate let-$data=\"data\" let-$options=\"options\" let-$index=\"index\">\n  <span>{{$data.row.text}}</span>\n</ng-template>\n", components: [{ type: i2.BentoComboboxComponent, selector: "bento-combobox", inputs: ["appendTo", "options", "up", "readonly", "open", "itemsObservable", "required", "revertOnInvalidInput", "rowTemplate", "id", "inputLabelText", "inputLabelClass"], outputs: ["search", "resetItems", "endOfScroll", "openChange"] }], directives: [{ type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], pipes: { "translate": i1.TranslatePipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectImportSourceComponent, decorators: [{
            type: Component,
            args: [{ selector: 'select-import-source', template: "<div class=\"form-group\">\n  <label for=\"basic-combobox\"> {{ 'bm.import.wizard.selectImportSource.label' | translate }} </label>\n  <bento-combobox\n    [(ngModel)]=\"selectedImportSource\"\n    [itemsObservable]=\"comboboxItems\"\n    [options]=\"comboboxOptions\"\n    [rowTemplate]=\"importSourceComboboxTemplate\"\n    [placeholder]=\"false\"\n    (ngModelChange)=\"onSelectionChanged($event)\"\n  >\n  </bento-combobox>\n</div>\n\n<ng-template #importSourceComboboxTemplate let-$data=\"data\" let-$options=\"options\" let-$index=\"index\">\n  <span>{{$data.row.text}}</span>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: SelectFileService }, { type: i1.TranslateService }]; } });

class SelectFileFromDriveComponent {
    constructor(selectFileService) {
        this.selectFileService = selectFileService;
    }
    onFileSelectChange(selectedFiles) {
        this.selectFileService.selectFilesFromDrive(selectedFiles);
    }
}
SelectFileFromDriveComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectFileFromDriveComponent, deps: [{ token: SelectFileService }], target: i0.ɵɵFactoryTarget.Component });
SelectFileFromDriveComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: SelectFileFromDriveComponent, selector: "select-file-from-drive", inputs: { options: "options" }, ngImport: i0, template: "<bento-file-selector\n  [(ngModel)]=\"selectedFiles\"\n  (ngModelChange)=\"onFileSelectChange($event)\"\n  [accept]=\"options.accept\"\n  [showSelectedFiles]=\"options.showSelectedFiles\"\n  [multiple]=\"options.allowMultiple\"\n  [showFileSize]=\"options.showFileSize\"\n  [appendOnSelection]=\"options.appendOnSelection\"\n  [disableDuringUpload]=\"options.disableDuringUpload\"\n  [showProgressPercentage]=\"options.showProgressPercentage\"\n>\n</bento-file-selector>\n", components: [{ type: i2.BentoFileSelectorComponent, selector: "bento-file-selector", inputs: ["accept", "appendOnSelection", "browseButtonLabel", "disableDuringUpload", "dropFieldLabel", "fileType", "multiple", "showSelectedFiles", "showFileSize", "showProgressPercentage", "uploadProgress"], outputs: ["fileSelect", "duplicateSelect", "fileRemove"] }], directives: [{ type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectFileFromDriveComponent, decorators: [{
            type: Component,
            args: [{ selector: 'select-file-from-drive', template: "<bento-file-selector\n  [(ngModel)]=\"selectedFiles\"\n  (ngModelChange)=\"onFileSelectChange($event)\"\n  [accept]=\"options.accept\"\n  [showSelectedFiles]=\"options.showSelectedFiles\"\n  [multiple]=\"options.allowMultiple\"\n  [showFileSize]=\"options.showFileSize\"\n  [appendOnSelection]=\"options.appendOnSelection\"\n  [disableDuringUpload]=\"options.disableDuringUpload\"\n  [showProgressPercentage]=\"options.showProgressPercentage\"\n>\n</bento-file-selector>\n" }]
        }], ctorParameters: function () { return [{ type: SelectFileService }]; }, propDecorators: { options: [{
                type: Input
            }] } });

function sameKey(a, b) {
    if (Boolean(a) && Boolean(b)) {
        // Both defined, compare IDs
        return a.id === b.id;
    }
    if (!a && !b) {
        // Neither defined, same
        return true;
    }
    // One is defined, one isn't
    return false;
}
function changed(prev, next) {
    return (prev.drawer !== next.drawer ||
        // Only compare clientId if we're going TO a client state
        (next.drawer === FileDrawer.Client && prev.clientId !== next.clientId) ||
        !sameKey(prev.container, next.container));
}

const drawerIcons = {
    [FileDrawer.Client]: IconType.Users,
    [FileDrawer.Firm]: IconType.Buildings,
    [FileDrawer.My]: IconType.User,
    [FileDrawer.All]: IconType.FolderGlobe,
};
class DrawerIconPipe {
    transform(drawer) {
        return drawerIcons[drawer];
    }
}
DrawerIconPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: DrawerIconPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
DrawerIconPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: DrawerIconPipe, name: "drawerIcon" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: DrawerIconPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'drawerIcon',
                }]
        }] });

class SelectFileFromDocumentsComponent {
    constructor(treeStore, selectFileService) {
        this.treeStore = treeStore;
        this.selectFileService = selectFileService;
        this.FileDrawer = FileDrawer;
        this.selectionChanged = new EventEmitter();
        this.treeLoading = false;
        this.drawersEnabled = true;
        this.touched = false;
        this.userAccount = { firmName: '', userName: '' };
    }
    ngOnChanges(changes) {
        var _a;
        if ((_a = changes.selection) === null || _a === void 0 ? void 0 : _a.currentValue) {
            this.updateSelection(changes.selection.currentValue);
        }
    }
    ngOnInit() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.selection = {
                drawer: this.options.primarySelectedDrawer,
                clientId: this.options.primarySelectedClientId,
            };
            if (this.selection) {
                this.treeRoot = yield this.getTreeRoot(this.selection);
            }
            this.drawersEnabled = yield hasSome([
                'OnvioDrive.ClientDocuments',
                'OnvioDrive.FirmDocuments',
                'OnvioDrive.MyDocuments',
            ]);
            const accounts = yield getAccounts({ active: true, onlyOnvio: true });
            this.userAccount = {
                userName: ((_a = accounts[0]) === null || _a === void 0 ? void 0 : _a.accountName) || '',
                firmName: ((_b = accounts[0]) === null || _b === void 0 ? void 0 : _b.companyName) || '',
            };
        });
    }
    drawerChanged(drawer) {
        return __awaiter(this, void 0, void 0, function* () {
            // We're going to clear the container
            // but we don't want to trigger a validation error
            // just because they switched drawers
            this.touched = false;
            yield this.updateSelection({ drawer, container: undefined });
        });
    }
    clientSelected(client) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.updateSelection({ clientId: client.id, container: undefined });
        });
    }
    folderSelected(selected) {
        return __awaiter(this, void 0, void 0, function* () {
            if (selected === null || selected === void 0 ? void 0 : selected.length) {
                const container = selected[0];
                yield this.updateSelection({ container });
                this.touched = true;
            }
            else {
                yield this.updateSelection({ container: undefined });
            }
        });
    }
    treeLoadingChanged(loading) {
        this.treeLoading = loading;
    }
    isClientDrawer() {
        return Boolean(this.selection) && isClientDrawer(this.selection.drawer);
    }
    setTouched(touched = true) {
        this.touched = touched;
    }
    updateSelection(change) {
        return __awaiter(this, void 0, void 0, function* () {
            const selection = Object.assign(Object.assign({}, this.selection), change);
            if (!this.selection || changed(this.selection, selection)) {
                this.selection = selection;
                this.treeRoot = yield this.getTreeRoot(this.selection);
                this.selectionChanged.emit(this.selection);
                if (this.selection.container && this.selection.container.type === ItemType.Document) {
                    const document = this.selection.container;
                    this.selectFileService.selectFileFromDocuments(document);
                }
                else {
                    this.selectFileService.selectFileFromDocuments(null);
                }
            }
        });
    }
    getTreeRoot(selection) {
        return __awaiter(this, void 0, void 0, function* () {
            if (selection.drawer === FileDrawer.Firm) {
                return this.getRootFolderKey(RootFolderType.FirmDocuments);
            }
            if (selection.drawer === FileDrawer.My) {
                return this.getRootFolderKey(RootFolderType.MyDocuments);
            }
            if (selection.drawer === FileDrawer.Client && selection.clientId) {
                return new ContainerKey(selection.clientId, ItemType.Client);
            }
            console.warn('invalid selection', selection);
        });
    }
    getRootFolderKey(type) {
        return __awaiter(this, void 0, void 0, function* () {
            const rootFolder = yield this.treeStore.getRootFolder(type);
            return new ContainerKey(rootFolder.id, ItemType.Folder);
        });
    }
}
SelectFileFromDocumentsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectFileFromDocumentsComponent, deps: [{ token: i1$2.TreeStore }, { token: SelectFileService }], target: i0.ɵɵFactoryTarget.Component });
SelectFileFromDocumentsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: SelectFileFromDocumentsComponent, selector: "select-file-from-documents", inputs: { options: "options" }, outputs: { selectionChanged: "selectionChanged" }, viewQueries: [{ propertyName: "tree", first: true, predicate: ["tree"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"file-selector\" *ngIf=\"drawersEnabled\">\n  <!-- Validation Border -->\n  <div *ngIf=\"touched && !selection.container\" class=\"tree-validation-border\"></div>\n\n  <!-- Drawer Header -->\n  <div class=\"drawer-header\" [ngSwitch]=\"selection.drawer\">\n    <span *ngSwitchCase=\"FileDrawer.Client\">\n      {{ 'bm.import.wizard.selectFileFromDocuments.clientDocs.header' | translate }}\n    </span>\n    <span *ngSwitchCase=\"FileDrawer.Firm\">\n      {{ 'bm.import.wizard.selectFileFromDocuments.firmDocs.header' | translate }}\n    </span>\n    <span *ngSwitchCase=\"FileDrawer.My\">\n      {{ 'bm.import.wizard.selectFileFromDocuments.myDocs.header' | translate }}\n    </span>\n  </div>\n\n  <!-- Tab Container -->\n  <div class=\"tab-container\">\n    <!-- Drawer Tabs -->\n    <div class=\"tabs\">\n      <bdc-drawer-tabs (change)=\"drawerChanged($event)\" [active]=\"selection.drawer\"></bdc-drawer-tabs>\n    </div>\n\n    <!-- Tree Container -->\n    <div class=\"tree-container\">\n      <!-- Tree Header -->\n      <div class=\"tree-header\">\n        <!-- Drawer Identifier -->\n        <div class=\"drawer-identifier\">\n          <!-- Drawer Icon -->\n          <bm-icon [type]=\"selection.drawer | drawerIcon\"></bm-icon>\n\n          <!-- Client Selector -->\n          <bdc-client-selector\n            (selectedChange)=\"clientSelected($event)\"\n            [selected]=\"{id: selection.clientId}\"\n            *ngIf=\"isClientDrawer()\"\n          >\n          </bdc-client-selector>\n\n          <!-- / Firm Name / My Name -->\n          <span class=\"drawer-owner\" *ngIf=\"!isClientDrawer()\">\n            {{ selection.drawer === FileDrawer.Firm ? userAccount.firmName : ''}} {{ selection.drawer === FileDrawer.My\n            ? userAccount.userName : ''}}\n          </span>\n        </div>\n      </div>\n\n      <!-- Tree -->\n      <div class=\"tree\" [hidden]=\"treeLoading\" *ngIf=\"selection.clientId || !isClientDrawer()\">\n        <bdc-document-tree\n          #tree\n          [root]=\"treeRoot\"\n          (loading)=\"treeLoadingChanged($event)\"\n          [showFiles]=\"true\"\n          [showRoot]=\"!isClientDrawer()\"\n          (selectedChange)=\"folderSelected($event)\"\n          [selected]=\"selection.container\"\n        >\n        </bdc-document-tree>\n      </div>\n\n      <div *ngIf=\"isClientDrawer() && !selection.clientId\">\n        <h4 style=\"margin-left: 20px\" class=\"validation danger\">\n          {{ 'bm.import.wizard.selectFileFromDocuments.selectClientError' | translate }}\n        </h4>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div *ngIf=\"!drawersEnabled\">\n  <span> {{ 'bm.import.wizard.selectFileFromDocuments.permissionsError' | translate }} </span>\n</div>\n", styles: [":host{display:flex;flex-direction:column}.file-selector{display:flex;flex:1;flex-direction:column;overflow:hidden;position:relative}.drawer-header{font-size:22px;padding-bottom:7px}.tab-container{display:flex;flex-direction:row;height:100%;justify-content:space-between;min-height:400px;overflow:hidden;padding-bottom:10px}.tree-container{display:flex;flex:1;flex-direction:column;max-width:100%;overflow:hidden}.tree-header{border-bottom:1px solid rgb(213,212,217);display:flex;flex-direction:row;justify-content:space-between;margin-bottom:4px;margin-top:4px;padding-bottom:0;padding-left:13px}.drawer-identifier bm-icon{height:16px;position:relative;top:3px}.drawer-identifier{flex:1}.drawer-owner{padding-left:6px}.drawer-identifier .drawer-owner{display:inline-block;font-size:16px;height:30px;line-height:30px;vertical-align:middle}.tree{flex:1;margin-left:5px;overflow:auto}.client-selector{padding-bottom:19px}bdc-client-selector{display:inline-block;min-width:500px;padding-left:6px;width:20%}label{font-family:Arial,Helvetica Neue,sans-serif;font-size:14px}.add-folder{color:#0072a8;cursor:pointer}.add-folder bm-icon{fill:#0072a8;position:relative;top:6px;width:14px}.add-folder label{cursor:pointer}.add-folder.disabled{color:#606169;cursor:not-allowed}.add-folder.disabled bm-icon{fill:#606169}.add-folder.disabled label{cursor:not-allowed}.validation{box-sizing:border-box;display:block;font-family:KnowledgeRegular,arial,helvetica,sans-serif;padding-top:5px;position:relative}.validation.danger{color:#dc0a0a}.validation.danger:before{content:url('data:image/svg+xml; utf8, <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"20\" height=\"20\" viewBox=\"0 0 24 24\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z\" fill=\"red\"/></svg>');position:relative;top:4px}.validation.info:before{content:url('data:image/svg+xml; utf8, <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"16\" height=\"16\" viewBox=\"0 -40 1024 984\" transform=\"scale(1, -1)\"><path d=\"M576 448v64h-192v-64h64v-192h-64v-64h256v64h-64v192zM448 704h128v-128h-128v128zM512 960c-281.6 0-512-230.4-512-512s230.4-512 512-512 512 230.4 512 512-230.4 512-512 512zM512 64c-211.2 0-384 172.8-384 384s172.8 384 384 384 384-172.8 384-384-172.8-384-384-384z\" /></svg>');position:relative;top:2px}.bm-input.ng-invalid.ng-dirty,.bm-input.ng-invalid.ng-touched{border-color:#a94442}.tree-validation-border{border-color:#dc0a0a;border-style:solid;border-width:1px;inset:-4px -8px;position:absolute;z-index:-1}\n"], components: [{ type: i3$1.DrawerTabsComponent, selector: "bdc-drawer-tabs", inputs: ["active", "showFirmDocumentsDrawer", "showMyDocumentsDrawer"], outputs: ["change"] }, { type: i3$1.ClientSelectorComponent, selector: "bdc-client-selector", inputs: ["required", "selected", "bubbleClientsUp", "disabled", "comboLabelText"], outputs: ["selectedChange"] }, { type: i3$1.DocumentTreeComponent, selector: "bdc-document-tree", inputs: ["selected", "treeEmptyMessage", "root", "drawer", "showFiles", "showRoot", "groupProjectsBy", "getHref", "canBeSelected"], outputs: ["selectedChange", "loading"] }], directives: [{ type: i7.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i7.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { type: i7.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }], pipes: { "translate": i1.TranslatePipe, "drawerIcon": DrawerIconPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectFileFromDocumentsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'select-file-from-documents', template: "<div class=\"file-selector\" *ngIf=\"drawersEnabled\">\n  <!-- Validation Border -->\n  <div *ngIf=\"touched && !selection.container\" class=\"tree-validation-border\"></div>\n\n  <!-- Drawer Header -->\n  <div class=\"drawer-header\" [ngSwitch]=\"selection.drawer\">\n    <span *ngSwitchCase=\"FileDrawer.Client\">\n      {{ 'bm.import.wizard.selectFileFromDocuments.clientDocs.header' | translate }}\n    </span>\n    <span *ngSwitchCase=\"FileDrawer.Firm\">\n      {{ 'bm.import.wizard.selectFileFromDocuments.firmDocs.header' | translate }}\n    </span>\n    <span *ngSwitchCase=\"FileDrawer.My\">\n      {{ 'bm.import.wizard.selectFileFromDocuments.myDocs.header' | translate }}\n    </span>\n  </div>\n\n  <!-- Tab Container -->\n  <div class=\"tab-container\">\n    <!-- Drawer Tabs -->\n    <div class=\"tabs\">\n      <bdc-drawer-tabs (change)=\"drawerChanged($event)\" [active]=\"selection.drawer\"></bdc-drawer-tabs>\n    </div>\n\n    <!-- Tree Container -->\n    <div class=\"tree-container\">\n      <!-- Tree Header -->\n      <div class=\"tree-header\">\n        <!-- Drawer Identifier -->\n        <div class=\"drawer-identifier\">\n          <!-- Drawer Icon -->\n          <bm-icon [type]=\"selection.drawer | drawerIcon\"></bm-icon>\n\n          <!-- Client Selector -->\n          <bdc-client-selector\n            (selectedChange)=\"clientSelected($event)\"\n            [selected]=\"{id: selection.clientId}\"\n            *ngIf=\"isClientDrawer()\"\n          >\n          </bdc-client-selector>\n\n          <!-- / Firm Name / My Name -->\n          <span class=\"drawer-owner\" *ngIf=\"!isClientDrawer()\">\n            {{ selection.drawer === FileDrawer.Firm ? userAccount.firmName : ''}} {{ selection.drawer === FileDrawer.My\n            ? userAccount.userName : ''}}\n          </span>\n        </div>\n      </div>\n\n      <!-- Tree -->\n      <div class=\"tree\" [hidden]=\"treeLoading\" *ngIf=\"selection.clientId || !isClientDrawer()\">\n        <bdc-document-tree\n          #tree\n          [root]=\"treeRoot\"\n          (loading)=\"treeLoadingChanged($event)\"\n          [showFiles]=\"true\"\n          [showRoot]=\"!isClientDrawer()\"\n          (selectedChange)=\"folderSelected($event)\"\n          [selected]=\"selection.container\"\n        >\n        </bdc-document-tree>\n      </div>\n\n      <div *ngIf=\"isClientDrawer() && !selection.clientId\">\n        <h4 style=\"margin-left: 20px\" class=\"validation danger\">\n          {{ 'bm.import.wizard.selectFileFromDocuments.selectClientError' | translate }}\n        </h4>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div *ngIf=\"!drawersEnabled\">\n  <span> {{ 'bm.import.wizard.selectFileFromDocuments.permissionsError' | translate }} </span>\n</div>\n", styles: [":host{display:flex;flex-direction:column}.file-selector{display:flex;flex:1;flex-direction:column;overflow:hidden;position:relative}.drawer-header{font-size:22px;padding-bottom:7px}.tab-container{display:flex;flex-direction:row;height:100%;justify-content:space-between;min-height:400px;overflow:hidden;padding-bottom:10px}.tree-container{display:flex;flex:1;flex-direction:column;max-width:100%;overflow:hidden}.tree-header{border-bottom:1px solid rgb(213,212,217);display:flex;flex-direction:row;justify-content:space-between;margin-bottom:4px;margin-top:4px;padding-bottom:0;padding-left:13px}.drawer-identifier bm-icon{height:16px;position:relative;top:3px}.drawer-identifier{flex:1}.drawer-owner{padding-left:6px}.drawer-identifier .drawer-owner{display:inline-block;font-size:16px;height:30px;line-height:30px;vertical-align:middle}.tree{flex:1;margin-left:5px;overflow:auto}.client-selector{padding-bottom:19px}bdc-client-selector{display:inline-block;min-width:500px;padding-left:6px;width:20%}label{font-family:Arial,Helvetica Neue,sans-serif;font-size:14px}.add-folder{color:#0072a8;cursor:pointer}.add-folder bm-icon{fill:#0072a8;position:relative;top:6px;width:14px}.add-folder label{cursor:pointer}.add-folder.disabled{color:#606169;cursor:not-allowed}.add-folder.disabled bm-icon{fill:#606169}.add-folder.disabled label{cursor:not-allowed}.validation{box-sizing:border-box;display:block;font-family:KnowledgeRegular,arial,helvetica,sans-serif;padding-top:5px;position:relative}.validation.danger{color:#dc0a0a}.validation.danger:before{content:url('data:image/svg+xml; utf8, <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"20\" height=\"20\" viewBox=\"0 0 24 24\"><path d=\"M0 0h24v24H0z\" fill=\"none\"/><path d=\"M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z\" fill=\"red\"/></svg>');position:relative;top:4px}.validation.info:before{content:url('data:image/svg+xml; utf8, <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"16\" height=\"16\" viewBox=\"0 -40 1024 984\" transform=\"scale(1, -1)\"><path d=\"M576 448v64h-192v-64h64v-192h-64v-64h256v64h-64v192zM448 704h128v-128h-128v128zM512 960c-281.6 0-512-230.4-512-512s230.4-512 512-512 512 230.4 512 512-230.4 512-512 512zM512 64c-211.2 0-384 172.8-384 384s172.8 384 384 384 384-172.8 384-384-172.8-384-384-384z\" /></svg>');position:relative;top:2px}.bm-input.ng-invalid.ng-dirty,.bm-input.ng-invalid.ng-touched{border-color:#a94442}.tree-validation-border{border-color:#dc0a0a;border-style:solid;border-width:1px;inset:-4px -8px;position:absolute;z-index:-1}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$2.TreeStore }, { type: SelectFileService }]; }, propDecorators: { tree: [{
                type: ViewChild,
                args: ['tree']
            }], options: [{
                type: Input
            }], selectionChanged: [{
                type: Output
            }] } });

class SelectFileComponent {
    constructor(selectFileService, selectFileFromDocumentsService, alertService) {
        this.selectFileService = selectFileService;
        this.selectFileFromDocumentsService = selectFileFromDocumentsService;
        this.alertService = alertService;
        this.subscription = new Subscription();
    }
    get importSourceEnum() {
        return ImportSource;
    }
    ngOnInit() {
        this.subscription.add(this.selectFileService.importSourceObservable.subscribe(importSource => {
            this.importSource = importSource;
            // Dismiss old alerts when import source has been changed
            this.alertService.dismissAll();
        }));
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    selectFile() {
        return __awaiter(this, void 0, void 0, function* () {
            // Dismiss old alerts before performing validation
            this.alertService.dismissAll();
            const isImportValid = this.validate();
            if (isImportValid && this.selectFileService.importSource === ImportSource.Documents) {
                const selectedFile = yield this.selectFileFromDocumentsService.getFileData(this.selectFileService.selectedFileMetadata);
                this.selectFileService.selectFilesFromDrive([selectedFile]);
            }
            return isImportValid;
        });
    }
    validate() {
        const isFileExtensionValid = this.validateFileExtension();
        const isFileSizeValid = this.validateFileSize();
        return isFileExtensionValid && isFileSizeValid;
    }
    validateFileExtension() {
        let isValid = true;
        const allowedExtensions = this.selectFileService.importSource === ImportSource.Computer
            ? this.options.selectFilesFromDriveOptions.accept
            : this.options.selectFilesFromDocumentsOptions.accept;
        const allowedExtensionsArr = allowedExtensions.split(',');
        const fileName = this.getSelectedFileName();
        isValid = allowedExtensionsArr.some(ext => fileName.endsWith(ext));
        if (!isValid) {
            this.alertService.addAlert('danger', {
                key: 'bm.import.wizard.validation.fileExtensionError',
                params: { allowedExtensions },
            });
        }
        return isValid;
    }
    validateFileSize() {
        let isValid = true;
        const maxAllowedFileSize = this.options.maxAllowedFileSize.value;
        const fileSize = this.getSelectedFileSize();
        isValid = fileSize <= maxAllowedFileSize;
        if (!isValid) {
            this.alertService.addAlert('danger', {
                key: 'bm.import.wizard.validation.fileSizeError',
                params: { maxAllowedFileSize: this.options.maxAllowedFileSize.text },
            });
        }
        return isValid;
    }
    getSelectedFileName() {
        return this.selectFileService.importSource === ImportSource.Computer
            ? this.selectFileService.selectedFiles[0].name
            : this.selectFileService.selectedFileMetadata.name;
    }
    getSelectedFileSize() {
        return this.selectFileService.importSource === ImportSource.Computer
            ? this.selectFileService.selectedFiles[0].size
            : this.selectFileService.selectedFileMetadata.size;
    }
}
SelectFileComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectFileComponent, deps: [{ token: SelectFileService }, { token: SelectFileFromDocumentsService }, { token: AlertService }], target: i0.ɵɵFactoryTarget.Component });
SelectFileComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: SelectFileComponent, selector: "select-file", inputs: { options: "options" }, ngImport: i0, template: "<div class=\"container-fluid\">\n  <div class=\"row\">\n    <select-import-source class=\"col-xl-3 col-md-4 col-sm-6 col-7\"></select-import-source>\n  </div>\n  <div class=\"row\" *ngIf=\"importSource === importSourceEnum.Computer\">\n    <div class=\"col\">\n      <select-file-from-drive [options]=\"options.selectFilesFromDriveOptions\"></select-file-from-drive>\n    </div>\n  </div>\n  <div class=\"row\" *ngIf=\"importSource === importSourceEnum.Documents\">\n    <div class=\"col\">\n      <select-file-from-documents [options]=\"options.selectFilesFromDocumentsOptions\"></select-file-from-documents>\n    </div>\n  </div>\n</div>\n", components: [{ type: SelectImportSourceComponent, selector: "select-import-source" }, { type: SelectFileFromDriveComponent, selector: "select-file-from-drive", inputs: ["options"] }, { type: SelectFileFromDocumentsComponent, selector: "select-file-from-documents", inputs: ["options"], outputs: ["selectionChanged"] }], directives: [{ type: i7.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SelectFileComponent, decorators: [{
            type: Component,
            args: [{ selector: 'select-file', template: "<div class=\"container-fluid\">\n  <div class=\"row\">\n    <select-import-source class=\"col-xl-3 col-md-4 col-sm-6 col-7\"></select-import-source>\n  </div>\n  <div class=\"row\" *ngIf=\"importSource === importSourceEnum.Computer\">\n    <div class=\"col\">\n      <select-file-from-drive [options]=\"options.selectFilesFromDriveOptions\"></select-file-from-drive>\n    </div>\n  </div>\n  <div class=\"row\" *ngIf=\"importSource === importSourceEnum.Documents\">\n    <div class=\"col\">\n      <select-file-from-documents [options]=\"options.selectFilesFromDocumentsOptions\"></select-file-from-documents>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: SelectFileService }, { type: SelectFileFromDocumentsService }, { type: AlertService }]; }, propDecorators: { options: [{
                type: Input
            }] } });

class ImportStatusComponent {
    constructor(resolver) {
        this.resolver = resolver;
    }
    get childComponent() {
        return this.componentRef.instance;
    }
    ngOnInit() {
        this.createComponent(this.statusOptions.statusComponentType);
    }
    createComponent(statusComponent) {
        this.container.clear();
        const factory = this.resolver.resolveComponentFactory(statusComponent);
        this.componentRef = this.container.createComponent(factory);
    }
    finishImport() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.childComponent.finishImport();
        });
    }
}
ImportStatusComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusComponent, deps: [{ token: i0.ComponentFactoryResolver }], target: i0.ɵɵFactoryTarget.Component });
ImportStatusComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: ImportStatusComponent, selector: "import-status", inputs: { statusOptions: "statusOptions" }, viewQueries: [{ propertyName: "container", first: true, predicate: ["importStatusContainer"], descendants: true, read: ViewContainerRef, static: true }], ngImport: i0, template: "<template #importStatusContainer></template>\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusComponent, decorators: [{
            type: Component,
            args: [{ selector: 'import-status', template: "<template #importStatusContainer></template>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ComponentFactoryResolver }]; }, propDecorators: { statusOptions: [{
                type: Input
            }], container: [{
                type: ViewChild,
                args: ['importStatusContainer', { read: ViewContainerRef, static: true }]
            }] } });

class CsvDataService {
    constructor(translateService, importContextService) {
        this.translateService = translateService;
        this.importContextService = importContextService;
        this.currencyColumns = [
            this.translateService.instant('bm.import.wizard.wip.column.wipValue'),
            this.translateService.instant('bm.import.wizard.ledger.column.gross'),
            this.translateService.instant('bm.import.wizard.ledger.column.net'),
            this.translateService.instant('bm.import.wizard.ledger.column.vat'),
        ];
    }
    getCsvData(importData, skipFirstRow, locale) {
        const csvData = [];
        const headers = [];
        importData[0].forEach((currentValue, index) => {
            headers.push(this.getColumnHeader(index));
        });
        for (let rowIndex = skipFirstRow ? 1 : 0; rowIndex < importData.length; rowIndex++) {
            const rowData = importData[rowIndex];
            let csvRow = '';
            rowData.forEach((cellValue, cellIndex) => {
                if (cellValue instanceof Date) {
                    const dateString = moment(cellValue).locale(locale).format('L');
                    csvRow += `${dateString}`;
                }
                else if (this.currencyColumns.includes(headers[cellIndex]) && !isNaN(cellValue)) {
                    const currencyString = formatCurrency(cellValue, locale, getLocaleCurrencySymbol(locale));
                    csvRow += `"${currencyString}"`;
                }
                else if (cellValue) {
                    const valueString = cellValue.toString().replace(/"/g, '""');
                    csvRow += `"${valueString}"`;
                }
                if (cellIndex < rowData.length - 1) {
                    csvRow += ',';
                }
            });
            csvData.push(csvRow);
        }
        return csvData;
    }
    getColumnHeader(colIndex) {
        const columnsMappings = this.importContextService.columnsMappings;
        if (columnsMappings.has(colIndex)) {
            return columnsMappings.get(colIndex).text;
        }
        return this.translateService.instant('bm.import.wizard.common.notUsed');
    }
}
CsvDataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: CsvDataService, deps: [{ token: i1.TranslateService }, { token: ImportContextService }], target: i0.ɵɵFactoryTarget.Injectable });
CsvDataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: CsvDataService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: CsvDataService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.TranslateService }, { type: ImportContextService }]; } });

class ValidationReportService {
    constructor(locale, importContextService, csvDataService, translateService) {
        this.importContextService = importContextService;
        this.csvDataService = csvDataService;
        this.translateService = translateService;
        this.locale = locale.toLowerCase();
    }
    getValidationCSV(errors) {
        const yesText = this.translateService.instant('bm.import.wizard.step.validate.yes');
        const sheetData = this.importContextService.sheetData;
        const skipFirstRow = this.importContextService.skipFirstRow;
        let csvReportString = String.fromCharCode(0xfeff); // Excel doesn't display some symbols correctly without BOM specified
        const issuesText = this.translateService.instant('bm.import.wizard.step.validate.issues');
        const howToFixText = this.translateService.instant('bm.import.wizard.step.validate.howToFix');
        if (skipFirstRow) {
            csvReportString += `${sheetData[0].join()},${issuesText},${howToFixText}\r\n`;
        }
        else {
            csvReportString += ','.repeat(sheetData[0].length) + `${issuesText},${howToFixText}\r\n`;
        }
        const csvData = this.csvDataService.getCsvData(sheetData, skipFirstRow, this.locale);
        for (let csvRowIndex = 0; csvRowIndex < csvData.length; csvRowIndex++) {
            const sheetDataRowIndex = skipFirstRow ? csvRowIndex + 1 : csvRowIndex;
            csvReportString += csvData[csvRowIndex] + ',';
            if (errors.has(sheetDataRowIndex)) {
                const rowErrors = Array.from(errors.get(sheetDataRowIndex).values());
                const howToFixMessage = rowErrors.map(this.mapValidationError).join(';');
                csvReportString += `${yesText},"${howToFixMessage}"\r\n`;
            }
            else {
                csvReportString += ',\r\n';
            }
        }
        return csvReportString;
    }
    mapValidationError(error) {
        return error.howToFix.join(';');
    }
}
ValidationReportService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ValidationReportService, deps: [{ token: LOCALE_ID }, { token: ImportContextService }, { token: CsvDataService }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
ValidationReportService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ValidationReportService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ValidationReportService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [LOCALE_ID]
                    }] }, { type: ImportContextService }, { type: CsvDataService }, { type: i1.TranslateService }];
    } });

class ImportWizardService {
    constructor() {
        /**
         * This is a the store of the registry where all possible imports are stored with an Type
         */
        this.imports = new Map();
    }
    /**
     * Adds a new import to the registry and fails of the type is already used and the value is different
     * @param type - Type of the import to register (must be unique)
     * @param importToAdd - Custom import logic
     * @returns undefined
     */
    addImport(type, importToAdd) {
        if (this.imports.has(type) && this.imports.get(type) !== importToAdd) {
            throw new Error(`The import ${type} is already registered with other value`);
        }
        this.imports.set(type, importToAdd);
    }
    /**
     * Removes import from the registry (useful on onDestroy and clean up phases)
     * @param type - Import type to remove
     * @returns undefined
     */
    removeImport(type) {
        if (this.imports.has(type)) {
            this.imports.delete(type);
        }
    }
    /**
     * Returns import options from the registry
     * @param type - Type of the import
     */
    getImportOptions(type) {
        this.assertImportType(type);
        return this.imports.get(type);
    }
    /**
     * Throws an error if import type isn't registered
     * @param type - Type of the import
     */
    assertImportType(type) {
        if (!this.imports.has(type)) {
            throw new Error(`The import ${type} is not registered`);
        }
    }
}
ImportWizardService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportWizardService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ImportWizardService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportWizardService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportWizardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ModalColumnMappingComponent {
    constructor(activeModal) {
        this.activeModal = activeModal;
    }
    ngOnInit() {
        this.columnTypes = new BehaviorSubject(this.columnMappingOptions);
        this.comboboxOptions = {
            labelFormatter: row => row.text,
        };
        this.selectedColumnType = this.existingMapping;
    }
    onSelectionChanged(selectedRow) {
        this.selectedColumnType = selectedRow;
    }
    save() {
        this.activeModal.close({
            columnType: this.selectedColumnType,
        });
    }
}
ModalColumnMappingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ModalColumnMappingComponent, deps: [{ token: i1$3.NgbActiveModal }], target: i0.ɵɵFactoryTarget.Component });
ModalColumnMappingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: ModalColumnMappingComponent, selector: "modal-column-mapping", inputs: { existingMapping: "existingMapping", columnMappingOptions: "columnMappingOptions" }, ngImport: i0, template: "<div>\n  <button type=\"button\" class=\"close\" aria-label=\"Close\" (click)=\"activeModal.close()\">\n    <i class=\"bento-icon-close-x\" aria-hidden=\"true\"></i>\n  </button>\n  <h3 id=\"modal-basic-title\" class=\"modal-header\">{{ \"bm.import.wizard.step.map.columnDetails\" | translate }}</h3>\n  <div class=\"modal-body\">\n    <div class=\"form-horizontal\">\n      <div class=\"form-group\">\n        <label for=\"basic-combobox\">{{ \"bm.import.wizard.step.map.columnType\" | translate }}</label>\n        <bento-combobox\n          [(ngModel)]=\"selectedColumnType\"\n          [itemsObservable]=\"columnTypes\"\n          [options]=\"comboboxOptions\"\n          [rowTemplate]=\"columnTypeComboboxTemplate\"\n          [placeholder]=\"false\"\n          (ngModelChange)=\"onSelectionChanged($event)\"\n        >\n        </bento-combobox>\n      </div>\n    </div>\n  </div>\n  <div class=\"modal-footer\">\n    <button type=\"button\" class=\"btn btn-primary\" (click)=\"save()\">\n      {{ \"bm.import.wizard.common.save\" | translate }}\n    </button>\n    <button type=\"button\" class=\"btn btn-outline-primary\" (click)=\"activeModal.close()\">\n      {{ \"bm.import.wizard.common.cancel\" | translate }}\n    </button>\n  </div>\n</div>\n\n<ng-template #columnTypeComboboxTemplate let-$data=\"data\" let-$options=\"options\" let-$index=\"index\">\n  <span [ngClass]=\"{ 'mandatory-list-option': $data.row.isMandatory }\">{{ $data.row.text }}</span>\n</ng-template>\n", styles: ["span.mandatory-list-option:after{content:\" *\";display:inline}\n"], components: [{ type: i2.BentoComboboxComponent, selector: "bento-combobox", inputs: ["appendTo", "options", "up", "readonly", "open", "itemsObservable", "required", "revertOnInvalidInput", "rowTemplate", "id", "inputLabelText", "inputLabelClass"], outputs: ["search", "resetItems", "endOfScroll", "openChange"] }], directives: [{ type: i2.BentoFocusMonitorDirective, selector: "a, button.btn, button.close, button.dropdown-item, a.dropdown-item, button.bento-number-input-button, button.bento-toast-action-btn" }, { type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i2.BentoFooterTabHelper, selector: "[class=\"modal-footer\"]" }, { type: i7.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], pipes: { "translate": i1.TranslatePipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ModalColumnMappingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'modal-column-mapping', template: "<div>\n  <button type=\"button\" class=\"close\" aria-label=\"Close\" (click)=\"activeModal.close()\">\n    <i class=\"bento-icon-close-x\" aria-hidden=\"true\"></i>\n  </button>\n  <h3 id=\"modal-basic-title\" class=\"modal-header\">{{ \"bm.import.wizard.step.map.columnDetails\" | translate }}</h3>\n  <div class=\"modal-body\">\n    <div class=\"form-horizontal\">\n      <div class=\"form-group\">\n        <label for=\"basic-combobox\">{{ \"bm.import.wizard.step.map.columnType\" | translate }}</label>\n        <bento-combobox\n          [(ngModel)]=\"selectedColumnType\"\n          [itemsObservable]=\"columnTypes\"\n          [options]=\"comboboxOptions\"\n          [rowTemplate]=\"columnTypeComboboxTemplate\"\n          [placeholder]=\"false\"\n          (ngModelChange)=\"onSelectionChanged($event)\"\n        >\n        </bento-combobox>\n      </div>\n    </div>\n  </div>\n  <div class=\"modal-footer\">\n    <button type=\"button\" class=\"btn btn-primary\" (click)=\"save()\">\n      {{ \"bm.import.wizard.common.save\" | translate }}\n    </button>\n    <button type=\"button\" class=\"btn btn-outline-primary\" (click)=\"activeModal.close()\">\n      {{ \"bm.import.wizard.common.cancel\" | translate }}\n    </button>\n  </div>\n</div>\n\n<ng-template #columnTypeComboboxTemplate let-$data=\"data\" let-$options=\"options\" let-$index=\"index\">\n  <span [ngClass]=\"{ 'mandatory-list-option': $data.row.isMandatory }\">{{ $data.row.text }}</span>\n</ng-template>\n", styles: ["span.mandatory-list-option:after{content:\" *\";display:inline}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$3.NgbActiveModal }]; }, propDecorators: { existingMapping: [{
                type: Input
            }], columnMappingOptions: [{
                type: Input
            }] } });

class ImportGridComponent {
    constructor(modalService, columnMappingService, importContextService, alertService) {
        this.modalService = modalService;
        this.columnMappingService = columnMappingService;
        this.importContextService = importContextService;
        this.alertService = alertService;
        this.itemFormatter = (panel, row, column, cell) => {
            if (panel.cellType === wjcGrid.CellType.Cell) {
                const grayColor = '#e8eaec';
                const lineThrough = 'line-through';
                if (this.skipFirstRowLocal && row === 0) {
                    cell.style.backgroundColor = grayColor;
                    cell.style.textDecoration = lineThrough;
                }
                else {
                    cell.style.backgroundColor = '';
                    cell.style.textDecoration = '';
                }
            }
        };
    }
    set skipFirstRow(value) {
        this.skipFirstRowLocal = value;
        if (this.flexGrid) {
            this.flexGrid.refresh(false);
        }
    }
    get sheetData() {
        return this.sheetDataLocal;
    }
    set sheetData(value) {
        this.sheetDataLocal = value;
        this.alertService.dismissAll();
        this.allowColumnMapping = this.validateRowsCount(this.sheetDataLocal);
        // Creates the wijmo data source
        this.wijmoData = new CollectionView(this.sheetDataLocal, {
            pageSize: 10,
        });
        this.columnMappingService.initializeColumnMappings(this.mappingColumnOptions, this.automapColumns ? this.sheetData[0] : null);
    }
    ngOnChanges(changes) {
        if ('skipFirstRow' in changes &&
            !changes.skipFirstRow.previousValue &&
            Boolean(changes.skipFirstRow.currentValue)) {
            if (this.automapColumns) {
                this.columnMappingService.setAutomappedColumns(this.sheetData[0]);
            }
        }
    }
    // This function is here to modify some styles and limitations that can be done by HTML directives
    wijmoInit(bentoflexGrid) {
        this.flexGrid = bentoflexGrid;
        if (this.flexGrid) {
            this.flexGrid.rowHeaders.columns[0].width = 35;
        }
    }
    editMapping(columnIndex) {
        // Open modal
        const containerSelector = this.columnMappingModalContainer || 'import-wizard';
        const modalRef = this.modalService.open(ModalColumnMappingComponent, {
            backdrop: 'static',
            container: containerSelector,
        });
        modalRef.componentInstance.existingMapping = this.columnMappingService.getColumnMapping(columnIndex);
        modalRef.componentInstance.columnMappingOptions = this.columnMappingService.getAvailableColumnMappings(columnIndex);
        modalRef.result.then(result => {
            if (result) {
                this.onMappingChanged(columnIndex, result.columnType);
            }
        });
    }
    getColumnHeader(columnIndex) {
        return this.columnMappingService.getColumnMapping(columnIndex).text;
    }
    getColumnFormat(colIndex) {
        const options = this.getColumnDisplayOptions(colIndex);
        if (options !== null && options !== undefined) {
            return options.format;
        }
        return null;
    }
    getColumnDisplayOptions(colIndex) {
        const columnsMappings = this.importContextService.columnsMappings;
        if (columnsMappings.has(colIndex)) {
            return columnsMappings.get(colIndex).displayOptions;
        }
        return null;
    }
    onMappingChanged(columnIndex, columnOptions) {
        this.columnMappingService.setColumnMapping(columnIndex, columnOptions);
    }
    validateRowsCount(sheedData) {
        let isValid = true;
        if (sheedData !== null) {
            isValid = sheedData.length <= this.maxAllowedRowCount.value;
            if (!isValid) {
                this.alertService.addAlert('danger', {
                    key: 'bm.import.wizard.validation.rowCountError',
                    params: { sheetName: this.importContextService.selectedSheet, maxRowCount: this.maxAllowedRowCount.text },
                });
            }
        }
        return isValid;
    }
}
ImportGridComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportGridComponent, deps: [{ token: i1$3.NgbModal }, { token: ColumnMappingService }, { token: ImportContextService }, { token: AlertService }], target: i0.ɵɵFactoryTarget.Component });
ImportGridComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: ImportGridComponent, selector: "import-grid", inputs: { mappingColumnOptions: "mappingColumnOptions", maxAllowedRowCount: "maxAllowedRowCount", automapColumns: "automapColumns", columnMappingModalContainer: "columnMappingModalContainer", skipFirstRow: "skipFirstRow", sheetData: "sheetData" }, usesOnChanges: true, ngImport: i0, template: "<wj-flex-grid\n  #bentoflexGrid\n  [isReadOnly]=\"true\"\n  [autoGenerateColumns]=\"false\"\n  [allowAddNew]=\"false\"\n  [allowDelete]=\"false\"\n  [allowDragging]=\"0\"\n  [allowMerging]=\"0\"\n  [allowResizing]=\"1\"\n  [deferResizing]=\"true\"\n  [allowSorting]=\"false\"\n  [selectionMode]=\"0\"\n  [itemFormatter]=\"itemFormatter\"\n  (initialized)=\"wijmoInit(bentoflexGrid)\"\n  [itemsSource]=\"wijmoData\"\n  class=\"bento-flex-grid\"\n>\n  <wj-flex-grid-column\n    *ngFor=\"let column of sheetData[0]; let index = index\"\n    [width]=\"'*'\"\n    [minWidth]=\"170\"\n    binding=\"{{ index }}\"\n    [format]=\"getColumnFormat(index)\"\n  >\n    <ng-template wjFlexGridCellTemplate [cellType]=\"'ColumnHeader'\">\n      <div *ngIf=\"allowColumnMapping; else notClickableHeader\" class=\"iw-header-container\" (click)=\"editMapping(index)\">\n        <div class=\"iw-header-text\">\n          <div>{{ getColumnHeader(index) }}</div>\n        </div>\n        <div class=\"iw-header-button\">\n          <a><i class=\"bento-icon-edit\"></i></a>\n        </div>\n      </div>\n      <ng-template #notClickableHeader>\n        <div class=\"iw-header-container iw-header-notclickable\">\n          <div class=\"iw-header-text\">\n            <div>{{ getColumnHeader(index) }}</div>\n          </div>\n        </div>\n      </ng-template>\n    </ng-template>\n  </wj-flex-grid-column>\n</wj-flex-grid>\n", styles: [".iw-header-container{display:flex}.iw-header-container:hover{cursor:pointer}.iw-header-container.iw-header-notclickable:hover{cursor:auto}.iw-header-text{flex-grow:8}.iw-header-text div{text-align:left!important}.iw-header-button{align-self:flex-start;padding-left:3px}\n"], components: [{ type: i5.WjFlexGrid, selector: "wj-flex-grid", inputs: ["wjModelProperty", "isDisabled", "tabOrder", "newRowAtTop", "allowAddNew", "allowDelete", "allowDragging", "allowMerging", "allowResizing", "allowSorting", "allowPinning", "autoScroll", "autoRowHeights", "autoSizeMode", "autoGenerateColumns", "autoSearch", "caseSensitiveSearch", "quickAutoSize", "bigCheckboxes", "childItemsPath", "groupHeaderFormat", "headersVisibility", "showSelectedHeaders", "showMarquee", "showPlaceholders", "itemFormatter", "isReadOnly", "imeEnabled", "mergeManager", "selectionMode", "showGroups", "showSort", "showDropDown", "showAlternatingRows", "showErrors", "alternatingRowStep", "itemValidator", "validateEdits", "treeIndent", "itemsSource", "autoClipboard", "expandSelectionOnCopyPaste", "frozenCells", "frozenRows", "frozenColumns", "cloneFrozenCells", "deferResizing", "sortRowIndex", "editColumnIndex", "stickyHeaders", "preserveSelectedState", "preserveOutlineState", "preserveWhiteSpace", "keyActionTab", "keyActionEnter", "rowHeaderPath", "virtualizationThreshold", "anchorCursor", "lazyRender", "refreshOnEdit", "copyHeaders", "columnGroups", "commitEmptyEdits", "headersFocusability", "ariaLabel", "skipMerged", "pasteEmptyCells", "groupSummaryPosition"], outputs: ["initialized", "gotFocus", "lostFocus", "refreshing", "refreshed", "invalidInput", "beginningEdit", "cellEditEnded", "cellEditEnding", "prepareCellForEdit", "formatItem", "resizingColumn", "resizedColumn", "autoSizingColumn", "autoSizedColumn", "draggingColumn", "draggingColumnOver", "draggedColumn", "sortingColumn", "sortedColumn", "pinningColumn", "pinnedColumn", "resizingRow", "resizedRow", "autoSizingRow", "autoSizedRow", "draggingRow", "draggingRowOver", "draggedRow", "deletingRow", "deletedRow", "loadingRows", "loadedRows", "rowEditStarting", "rowEditStarted", "rowEditEnding", "rowEditEnded", "rowAdded", "groupCollapsedChanging", "groupCollapsedChanged", "columnGroupCollapsedChanging", "columnGroupCollapsedChanged", "itemsSourceChanging", "itemsSourceChanged", "selectionChanging", "selectionChanged", "scrollPositionChanged", "updatingView", "updatedView", "updatingLayout", "updatedLayout", "pasting", "pasted", "pastingCell", "pastedCell", "copying", "copied"] }, { type: i5.WjFlexGridColumn, selector: "wj-flex-grid-column", inputs: ["asyncBindings", "wjProperty", "name", "dataMap", "dataType", "binding", "sortMemberPath", "format", "cellTemplate", "header", "width", "maxLength", "minWidth", "maxWidth", "align", "allowDragging", "allowSorting", "allowResizing", "allowMerging", "aggregate", "isReadOnly", "cssClass", "cssClassAll", "isContentHtml", "isSelected", "visible", "wordWrap", "multiLine", "mask", "inputType", "isRequired", "showDropDown", "dataMapEditor", "dropDownCssClass", "quickAutoSize", "editor"], outputs: ["initialized", "isSelectedChange"] }], directives: [{ type: i7.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i5.WjFlexGridCellTemplate, selector: "[wjFlexGridCellTemplate]", inputs: ["wjFlexGridCellTemplate", "cellOverflow", "valuePaths", "autoSizeRows", "forceFullEdit", "cellType"], exportAs: ["wjFlexGridCellTemplate"] }, { type: i7.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.BentoFocusMonitorDirective, selector: "a, button.btn, button.close, button.dropdown-item, a.dropdown-item, button.bento-number-input-button, button.bento-toast-action-btn" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportGridComponent, decorators: [{
            type: Component,
            args: [{ selector: 'import-grid', template: "<wj-flex-grid\n  #bentoflexGrid\n  [isReadOnly]=\"true\"\n  [autoGenerateColumns]=\"false\"\n  [allowAddNew]=\"false\"\n  [allowDelete]=\"false\"\n  [allowDragging]=\"0\"\n  [allowMerging]=\"0\"\n  [allowResizing]=\"1\"\n  [deferResizing]=\"true\"\n  [allowSorting]=\"false\"\n  [selectionMode]=\"0\"\n  [itemFormatter]=\"itemFormatter\"\n  (initialized)=\"wijmoInit(bentoflexGrid)\"\n  [itemsSource]=\"wijmoData\"\n  class=\"bento-flex-grid\"\n>\n  <wj-flex-grid-column\n    *ngFor=\"let column of sheetData[0]; let index = index\"\n    [width]=\"'*'\"\n    [minWidth]=\"170\"\n    binding=\"{{ index }}\"\n    [format]=\"getColumnFormat(index)\"\n  >\n    <ng-template wjFlexGridCellTemplate [cellType]=\"'ColumnHeader'\">\n      <div *ngIf=\"allowColumnMapping; else notClickableHeader\" class=\"iw-header-container\" (click)=\"editMapping(index)\">\n        <div class=\"iw-header-text\">\n          <div>{{ getColumnHeader(index) }}</div>\n        </div>\n        <div class=\"iw-header-button\">\n          <a><i class=\"bento-icon-edit\"></i></a>\n        </div>\n      </div>\n      <ng-template #notClickableHeader>\n        <div class=\"iw-header-container iw-header-notclickable\">\n          <div class=\"iw-header-text\">\n            <div>{{ getColumnHeader(index) }}</div>\n          </div>\n        </div>\n      </ng-template>\n    </ng-template>\n  </wj-flex-grid-column>\n</wj-flex-grid>\n", styles: [".iw-header-container{display:flex}.iw-header-container:hover{cursor:pointer}.iw-header-container.iw-header-notclickable:hover{cursor:auto}.iw-header-text{flex-grow:8}.iw-header-text div{text-align:left!important}.iw-header-button{align-self:flex-start;padding-left:3px}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$3.NgbModal }, { type: ColumnMappingService }, { type: ImportContextService }, { type: AlertService }]; }, propDecorators: { mappingColumnOptions: [{
                type: Input
            }], maxAllowedRowCount: [{
                type: Input
            }], automapColumns: [{
                type: Input
            }], columnMappingModalContainer: [{
                type: Input
            }], skipFirstRow: [{
                type: Input
            }], sheetData: [{
                type: Input
            }] } });

class ImportMappingComponent {
    constructor(importContextService) {
        this.importContextService = importContextService;
    }
    ngOnInit() {
        this.sheetsNames = this.importContextService.sheetsNamesObservable;
        this.selectedSheet = this.importContextService.selectedSheetObservable;
        this.sheetData = this.importContextService.sheetDataObservable;
        this.skipFirstRow = this.importContextService.skipFirstRowObservable;
    }
    onSheetChanged(sheetName) {
        this.importContextService.selectedSheet = sheetName;
    }
    onCheckBoxChanged(isChecked) {
        this.importContextService.skipFirstRow = isChecked;
    }
}
ImportMappingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportMappingComponent, deps: [{ token: ImportContextService }], target: i0.ɵɵFactoryTarget.Component });
ImportMappingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: ImportMappingComponent, selector: "import-mapping", inputs: { mappingOptions: "mappingOptions", maxAllowedRowCount: "maxAllowedRowCount" }, ngImport: i0, template: "<div class=\"heading-message\">\n  <p [innerHTML]=\"'bm.import.wizard.step.map.description' | translate\"></p>\n</div>\n<div class=\"grid-header\">\n  <span>{{ \"bm.import.wizard.step.map.selectWorksheet\" | translate }}</span>\n  <div class=\"flex-col\">\n    <div>\n      <bento-checkbox [ngModel]=\"skipFirstRow | async\" (ngModelChange)=\"onCheckBoxChanged($event)\"></bento-checkbox>\n      <span class=\"align-middle\">{{ \"bm.import.wizard.step.map.skipFirstRow\" | translate }}</span>\n    </div>\n  </div>\n</div>\n<div type=\"pills\" class=\"bento-tabset tabs-top\">\n  <ul role=\"tablist\" class=\"nav nav-pills justify-content-start\">\n    <li\n      *ngFor=\"let sheetName of sheetsNames | async\"\n      (click)=\"onSheetChanged(sheetName)\"\n      class=\"nav-item ng-star-inserted\"\n    >\n      <a class=\"nav-link\" [ngClass]=\"{ active: sheetName === (selectedSheet | async) }\" role=\"tab\">\n        <span class=\"ng-star-inserted\">{{ sheetName }}</span>\n      </a>\n    </li>\n  </ul>\n</div>\n<div>\n  <import-grid\n    *ngIf=\"sheetData | async\"\n    [maxAllowedRowCount]=\"maxAllowedRowCount\"\n    [mappingColumnOptions]=\"mappingOptions.columnMappings\"\n    [sheetData]=\"sheetData | async\"\n    [skipFirstRow]=\"skipFirstRow | async\"\n    [automapColumns]=\"mappingOptions.allowAutomappingColumns && (skipFirstRow | async)\"\n    [columnMappingModalContainer]=\"mappingOptions.columnMappingModalContainer\"\n  ></import-grid>\n</div>\n", styles: [".heading-message{background-color:#fbfdff;border:1px solid rgba(0,0,0,.15);border-radius:3px;box-shadow:inset 0 1px 1px #0000000d;color:#000;margin-bottom:20px;min-height:20px;padding:9px 9px 0}.grid-header{display:flex;justify-content:space-between}.flex-col{align-items:flex-end;display:flex;flex-direction:column}\n"], components: [{ type: i2.BentoCheckboxComponent, selector: "bento-checkbox", inputs: ["indeterminate"], outputs: ["indeterminateChange"] }, { type: ImportGridComponent, selector: "import-grid", inputs: ["mappingColumnOptions", "maxAllowedRowCount", "automapColumns", "columnMappingModalContainer", "skipFirstRow", "sheetData"] }], directives: [{ type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i7.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i2.BentoFocusMonitorDirective, selector: "a, button.btn, button.close, button.dropdown-item, a.dropdown-item, button.bento-number-input-button, button.bento-toast-action-btn" }, { type: i7.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i7.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "translate": i1.TranslatePipe, "async": i7.AsyncPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportMappingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'import-mapping', template: "<div class=\"heading-message\">\n  <p [innerHTML]=\"'bm.import.wizard.step.map.description' | translate\"></p>\n</div>\n<div class=\"grid-header\">\n  <span>{{ \"bm.import.wizard.step.map.selectWorksheet\" | translate }}</span>\n  <div class=\"flex-col\">\n    <div>\n      <bento-checkbox [ngModel]=\"skipFirstRow | async\" (ngModelChange)=\"onCheckBoxChanged($event)\"></bento-checkbox>\n      <span class=\"align-middle\">{{ \"bm.import.wizard.step.map.skipFirstRow\" | translate }}</span>\n    </div>\n  </div>\n</div>\n<div type=\"pills\" class=\"bento-tabset tabs-top\">\n  <ul role=\"tablist\" class=\"nav nav-pills justify-content-start\">\n    <li\n      *ngFor=\"let sheetName of sheetsNames | async\"\n      (click)=\"onSheetChanged(sheetName)\"\n      class=\"nav-item ng-star-inserted\"\n    >\n      <a class=\"nav-link\" [ngClass]=\"{ active: sheetName === (selectedSheet | async) }\" role=\"tab\">\n        <span class=\"ng-star-inserted\">{{ sheetName }}</span>\n      </a>\n    </li>\n  </ul>\n</div>\n<div>\n  <import-grid\n    *ngIf=\"sheetData | async\"\n    [maxAllowedRowCount]=\"maxAllowedRowCount\"\n    [mappingColumnOptions]=\"mappingOptions.columnMappings\"\n    [sheetData]=\"sheetData | async\"\n    [skipFirstRow]=\"skipFirstRow | async\"\n    [automapColumns]=\"mappingOptions.allowAutomappingColumns && (skipFirstRow | async)\"\n    [columnMappingModalContainer]=\"mappingOptions.columnMappingModalContainer\"\n  ></import-grid>\n</div>\n", styles: [".heading-message{background-color:#fbfdff;border:1px solid rgba(0,0,0,.15);border-radius:3px;box-shadow:inset 0 1px 1px #0000000d;color:#000;margin-bottom:20px;min-height:20px;padding:9px 9px 0}.grid-header{display:flex;justify-content:space-between}.flex-col{align-items:flex-end;display:flex;flex-direction:column}\n"] }]
        }], ctorParameters: function () { return [{ type: ImportContextService }]; }, propDecorators: { mappingOptions: [{
                type: Input
            }], maxAllowedRowCount: [{
                type: Input
            }] } });

class ImportValidationComponent {
    constructor(importContextService, validationService, validationReportService, translateService, renderer) {
        this.importContextService = importContextService;
        this.validationService = validationService;
        this.validationReportService = validationReportService;
        this.translateService = translateService;
        this.renderer = renderer;
        this.sheetData = null;
        this.validationGridData = null;
        this.showGrid = false;
        this.tooltip = new wijmo.Tooltip();
        this.errorReportDisabled = true;
        this.subscription = new Subscription();
        this.itemFormatter = (panel, r, c, cell) => {
            if (wjcGrid.CellType.Cell === panel.cellType) {
                if (this.validationGridData[r][c].hasError) {
                    this.renderer.setStyle(cell, 'background-color', '#DC143C');
                    this.tooltip.setTooltip(cell, this.validationGridData[r][c].errorTooltip);
                    // Prevent formatting of invalid cells
                    let unformattedValue = this.validationGridData[r][c].value;
                    if (unformattedValue instanceof Date) {
                        unformattedValue = unformattedValue.toLocaleDateString();
                    }
                    this.renderer.setProperty(cell, 'textContent', unformattedValue);
                }
                else {
                    this.renderer.setStyle(cell, 'background-color', null);
                    this.tooltip.setTooltip(cell, null);
                }
            }
        };
    }
    ngOnInit() {
        this.subscription.add(this.validationService.isFileValidObservable.subscribe((isFileValid) => {
            this.refreshGridData();
            this.errorReportDisabled = isFileValid;
        }));
        this.subscription.add(this.importContextService.selectedSheetObservable.subscribe((name) => {
            this.sheetName = name;
        }));
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.tooltip.dispose();
    }
    // This function is here to modify some styles and limitations that can be done by HTML directives
    wijmoInit(grid) {
        this.flexGrid = grid;
        this.flexGrid.rowHeaders.columns.defaultSize = 75;
    }
    getColumnHeader(colIndex) {
        const columnsMappings = this.importContextService.columnsMappings;
        if (columnsMappings.has(colIndex)) {
            return columnsMappings.get(colIndex).text;
        }
        return this.translateService.instant('bm.import.wizard.common.notUsed');
    }
    getColumnFormat(colIndex) {
        const options = this.getColumnDisplayOptions(colIndex);
        if (options !== null && options !== undefined) {
            return options.format;
        }
        return null;
    }
    getColumnAlignment(colIndex) {
        const options = this.getColumnDisplayOptions(colIndex);
        if (options !== null && options !== undefined) {
            return options.align;
        }
        return 'left';
    }
    rowHasError(rowIndex) {
        return this.validationGridData[rowIndex].some(it => it.hasError);
    }
    getErrorReport() {
        const csv = this.validationReportService.getValidationCSV(this.validationService.validationErrors);
        const csvBlob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(csvBlob, 'error_report.csv');
    }
    refreshGridData() {
        this.validationGridData = this.validationService.getValidationGridData();
        if (this.validationGridData === null) {
            this.sheetData = null;
        }
        else {
            this.sheetData = this.validationGridData.map(c => c.map(d => d.value));
            this.applyCustomValueConverter();
        }
        this.wijmoData = new CollectionView(this.sheetData, {});
    }
    applyCustomValueConverter() {
        this.importContextService.columnsMappings.forEach((value, key) => {
            var _a, _b;
            if (((_a = value === null || value === void 0 ? void 0 : value.displayOptions) === null || _a === void 0 ? void 0 : _a.customValueConverter) !== null &&
                ((_b = value === null || value === void 0 ? void 0 : value.displayOptions) === null || _b === void 0 ? void 0 : _b.customValueConverter) !== undefined) {
                this.sheetData.forEach(data => {
                    data[key] = value.displayOptions.customValueConverter.convertValue(data[key]);
                });
            }
        });
    }
    getColumnDisplayOptions(colIndex) {
        const columnsMappings = this.importContextService.columnsMappings;
        if (columnsMappings.has(colIndex)) {
            return columnsMappings.get(colIndex).displayOptions;
        }
        return null;
    }
}
ImportValidationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportValidationComponent, deps: [{ token: ImportContextService }, { token: ValidationService }, { token: ValidationReportService }, { token: i1.TranslateService }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
ImportValidationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: ImportValidationComponent, selector: "import-validation", ngImport: i0, template: "<div>\n  <div type=\"pills\" class=\"bento-tabset tabs-top\">\n    <ul role=\"tablist\" class=\"nav nav-pills justify-content-start float-left\">\n      <li class=\"nav-item ng-star-inserted\">\n        <label class=\"nav-link active\" role=\"tab\">\n          <span class=\"ng-star-inserted\">{{ sheetName }}</span>\n        </label>\n      </li>\n    </ul>\n  </div>\n  <div class=\"btn-error-report-container\">\n    <button\n      type=\"button\"\n      class=\"btn btn-outline-primary\"\n      (click)=\"getErrorReport()\"\n      [disabled]=\"errorReportDisabled\"\n      [ngbTooltip]=\"\n        'bm.import.wizard.step.validate.errorReportTooltip' | translate\n      \"\n    >\n      {{ \"bm.import.wizard.step.validate.errorReport\" | translate }}\n    </button>\n  </div>\n</div>\n\n<div *ngIf=\"sheetData\">\n  <wj-flex-grid\n    #validationGrid\n    [isReadOnly]=\"true\"\n    [autoGenerateColumns]=\"false\"\n    [allowAddNew]=\"false\"\n    [allowDelete]=\"false\"\n    [allowDragging]=\"0\"\n    [allowMerging]=\"0\"\n    [allowResizing]=\"1\"\n    [deferResizing]=\"true\"\n    [allowSorting]=\"false\"\n    [selectionMode]=\"0\"\n    [itemFormatter]=\"itemFormatter\"\n    (initialized)=\"wijmoInit(validationGrid)\"\n    [itemsSource]=\"wijmoData\"\n    class=\"bento-flex-grid\"\n  >\n    <ng-template wjFlexGridCellTemplate cellType=\"TopLeft\">\n      {{ \"bm.import.wizard.common.issues\" | translate }}\n    </ng-template>\n\n    <ng-template wjFlexGridCellTemplate cellType=\"RowHeader\" let-cell=\"cell\">\n      <div class=\"d-flex justify-content-center\" *ngIf=\"rowHasError(cell.row.index)\">\n        <i class=\"bento-icon-warning\" placement=\"top\" container=\"body\"></i>\n      </div>\n    </ng-template>\n\n    <wj-flex-grid-column\n      *ngFor=\"let column of sheetData[0]; let index = index\"\n      [width]=\"'*'\"\n      [minWidth]=\"170\"\n      binding=\"{{ index }}\"\n      [format]=\"getColumnFormat(index)\"\n      [align]=\"getColumnAlignment(index)\"\n    >\n      <ng-template wjFlexGridCellTemplate cellType=\"ColumnHeader\">\n        <div class=\"iw-header-container\">\n          <div class=\"iw-header-text\">\n            <div>{{ getColumnHeader(index) }}</div>\n          </div>\n        </div>\n      </ng-template>\n    </wj-flex-grid-column>\n  </wj-flex-grid>\n</div>\n", styles: [".btn-error-report-container{float:right;line-height:2.8571428571rem;margin:.7142857143rem 0}\n"], components: [{ type: i5.WjFlexGrid, selector: "wj-flex-grid", inputs: ["wjModelProperty", "isDisabled", "tabOrder", "newRowAtTop", "allowAddNew", "allowDelete", "allowDragging", "allowMerging", "allowResizing", "allowSorting", "allowPinning", "autoScroll", "autoRowHeights", "autoSizeMode", "autoGenerateColumns", "autoSearch", "caseSensitiveSearch", "quickAutoSize", "bigCheckboxes", "childItemsPath", "groupHeaderFormat", "headersVisibility", "showSelectedHeaders", "showMarquee", "showPlaceholders", "itemFormatter", "isReadOnly", "imeEnabled", "mergeManager", "selectionMode", "showGroups", "showSort", "showDropDown", "showAlternatingRows", "showErrors", "alternatingRowStep", "itemValidator", "validateEdits", "treeIndent", "itemsSource", "autoClipboard", "expandSelectionOnCopyPaste", "frozenCells", "frozenRows", "frozenColumns", "cloneFrozenCells", "deferResizing", "sortRowIndex", "editColumnIndex", "stickyHeaders", "preserveSelectedState", "preserveOutlineState", "preserveWhiteSpace", "keyActionTab", "keyActionEnter", "rowHeaderPath", "virtualizationThreshold", "anchorCursor", "lazyRender", "refreshOnEdit", "copyHeaders", "columnGroups", "commitEmptyEdits", "headersFocusability", "ariaLabel", "skipMerged", "pasteEmptyCells", "groupSummaryPosition"], outputs: ["initialized", "gotFocus", "lostFocus", "refreshing", "refreshed", "invalidInput", "beginningEdit", "cellEditEnded", "cellEditEnding", "prepareCellForEdit", "formatItem", "resizingColumn", "resizedColumn", "autoSizingColumn", "autoSizedColumn", "draggingColumn", "draggingColumnOver", "draggedColumn", "sortingColumn", "sortedColumn", "pinningColumn", "pinnedColumn", "resizingRow", "resizedRow", "autoSizingRow", "autoSizedRow", "draggingRow", "draggingRowOver", "draggedRow", "deletingRow", "deletedRow", "loadingRows", "loadedRows", "rowEditStarting", "rowEditStarted", "rowEditEnding", "rowEditEnded", "rowAdded", "groupCollapsedChanging", "groupCollapsedChanged", "columnGroupCollapsedChanging", "columnGroupCollapsedChanged", "itemsSourceChanging", "itemsSourceChanged", "selectionChanging", "selectionChanged", "scrollPositionChanged", "updatingView", "updatedView", "updatingLayout", "updatedLayout", "pasting", "pasted", "pastingCell", "pastedCell", "copying", "copied"] }, { type: i5.WjFlexGridColumn, selector: "wj-flex-grid-column", inputs: ["asyncBindings", "wjProperty", "name", "dataMap", "dataType", "binding", "sortMemberPath", "format", "cellTemplate", "header", "width", "maxLength", "minWidth", "maxWidth", "align", "allowDragging", "allowSorting", "allowResizing", "allowMerging", "aggregate", "isReadOnly", "cssClass", "cssClassAll", "isContentHtml", "isSelected", "visible", "wordWrap", "multiLine", "mask", "inputType", "isRequired", "showDropDown", "dataMapEditor", "dropDownCssClass", "quickAutoSize", "editor"], outputs: ["initialized", "isSelectedChange"] }], directives: [{ type: i2.BentoFocusMonitorDirective, selector: "a, button.btn, button.close, button.dropdown-item, a.dropdown-item, button.bento-number-input-button, button.bento-toast-action-btn" }, { type: i2.BentoNgbTooltipSupportDirective, selector: "[ngbTooltip]" }, { type: i1$3.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "triggers", "container", "disableTooltip", "tooltipClass", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { type: i7.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i5.WjFlexGridCellTemplate, selector: "[wjFlexGridCellTemplate]", inputs: ["wjFlexGridCellTemplate", "cellOverflow", "valuePaths", "autoSizeRows", "forceFullEdit", "cellType"], exportAs: ["wjFlexGridCellTemplate"] }, { type: i7.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], pipes: { "translate": i1.TranslatePipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportValidationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'import-validation', template: "<div>\n  <div type=\"pills\" class=\"bento-tabset tabs-top\">\n    <ul role=\"tablist\" class=\"nav nav-pills justify-content-start float-left\">\n      <li class=\"nav-item ng-star-inserted\">\n        <label class=\"nav-link active\" role=\"tab\">\n          <span class=\"ng-star-inserted\">{{ sheetName }}</span>\n        </label>\n      </li>\n    </ul>\n  </div>\n  <div class=\"btn-error-report-container\">\n    <button\n      type=\"button\"\n      class=\"btn btn-outline-primary\"\n      (click)=\"getErrorReport()\"\n      [disabled]=\"errorReportDisabled\"\n      [ngbTooltip]=\"\n        'bm.import.wizard.step.validate.errorReportTooltip' | translate\n      \"\n    >\n      {{ \"bm.import.wizard.step.validate.errorReport\" | translate }}\n    </button>\n  </div>\n</div>\n\n<div *ngIf=\"sheetData\">\n  <wj-flex-grid\n    #validationGrid\n    [isReadOnly]=\"true\"\n    [autoGenerateColumns]=\"false\"\n    [allowAddNew]=\"false\"\n    [allowDelete]=\"false\"\n    [allowDragging]=\"0\"\n    [allowMerging]=\"0\"\n    [allowResizing]=\"1\"\n    [deferResizing]=\"true\"\n    [allowSorting]=\"false\"\n    [selectionMode]=\"0\"\n    [itemFormatter]=\"itemFormatter\"\n    (initialized)=\"wijmoInit(validationGrid)\"\n    [itemsSource]=\"wijmoData\"\n    class=\"bento-flex-grid\"\n  >\n    <ng-template wjFlexGridCellTemplate cellType=\"TopLeft\">\n      {{ \"bm.import.wizard.common.issues\" | translate }}\n    </ng-template>\n\n    <ng-template wjFlexGridCellTemplate cellType=\"RowHeader\" let-cell=\"cell\">\n      <div class=\"d-flex justify-content-center\" *ngIf=\"rowHasError(cell.row.index)\">\n        <i class=\"bento-icon-warning\" placement=\"top\" container=\"body\"></i>\n      </div>\n    </ng-template>\n\n    <wj-flex-grid-column\n      *ngFor=\"let column of sheetData[0]; let index = index\"\n      [width]=\"'*'\"\n      [minWidth]=\"170\"\n      binding=\"{{ index }}\"\n      [format]=\"getColumnFormat(index)\"\n      [align]=\"getColumnAlignment(index)\"\n    >\n      <ng-template wjFlexGridCellTemplate cellType=\"ColumnHeader\">\n        <div class=\"iw-header-container\">\n          <div class=\"iw-header-text\">\n            <div>{{ getColumnHeader(index) }}</div>\n          </div>\n        </div>\n      </ng-template>\n    </wj-flex-grid-column>\n  </wj-flex-grid>\n</div>\n", styles: [".btn-error-report-container{float:right;line-height:2.8571428571rem;margin:.7142857143rem 0}\n"] }]
        }], ctorParameters: function () { return [{ type: ImportContextService }, { type: ValidationService }, { type: ValidationReportService }, { type: i1.TranslateService }, { type: i0.Renderer2 }]; } });

class AlertComponent {
    constructor(alertService) {
        this.alertService = alertService;
        this.alerts = [];
        this.subscription = new Subscription();
    }
    ngOnInit() {
        this.subscription.add(this.alertService.onAlertAdded.subscribe((option) => {
            this.alerts.push(option);
        }));
        this.subscription.add(this.alertService.onAllDismissed.subscribe(() => {
            this.alerts.splice(0, this.alerts.length);
        }));
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
AlertComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AlertComponent, deps: [{ token: AlertService }], target: i0.ɵɵFactoryTarget.Component });
AlertComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: AlertComponent, selector: "alert", ngImport: i0, template: "<div class=\"bottom-alerts-pane\">\n  <bento-alert [items]=\"alerts\" [inline]=\"true\"></bento-alert>\n</div>\n", styles: [".bottom-alerts-pane{bottom:0%;left:50%;margin-left:-250px;opacity:1;position:fixed;width:500px;z-index:2000}\n"], components: [{ type: i2.BentoAlertComponent, selector: "bento-alert", inputs: ["items", "inline", "grabFocus"], outputs: ["close"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AlertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'alert', template: "<div class=\"bottom-alerts-pane\">\n  <bento-alert [items]=\"alerts\" [inline]=\"true\"></bento-alert>\n</div>\n", styles: [".bottom-alerts-pane{bottom:0%;left:50%;margin-left:-250px;opacity:1;position:fixed;width:500px;z-index:2000}\n"] }]
        }], ctorParameters: function () { return [{ type: AlertService }]; } });

class ImportWizardComponent {
    constructor(importWizard, selectFileService, fileDataService, columnMappingService, validationService, alertService, changeDetectorRef) {
        this.importWizard = importWizard;
        this.selectFileService = selectFileService;
        this.fileDataService = fileDataService;
        this.columnMappingService = columnMappingService;
        this.validationService = validationService;
        this.alertService = alertService;
        this.changeDetectorRef = changeDetectorRef;
        this.hideTitle = false;
        this.finishImport = new EventEmitter();
        this.currentStep = 0;
    }
    ngOnInit() {
        return __awaiter(this, void 0, void 0, function* () {
            this.isMapStepEnabled = this.selectFileService.isFileSelectedObservable;
            this.isValidateStepEnabled = this.columnMappingService.isMandatoryColumnsMappedObservable.pipe(debounceTime(0), tap(() => {
                this.changeDetectorRef.detectChanges();
            }));
            this.isImportStepEnabled = this.validationService.isFileValidObservable;
            this.importOptions = this.importWizard.getImportOptions(this.type);
            if (this.importOptions.initialize) {
                yield this.importOptions.initialize();
            }
            this.importTitle = this.importOptions.importTitle;
        });
    }
    /**
     * Finish import button should be visible only on the last
     */
    isFinishImportButtonVisible() {
        return this.currentStep === 3;
    }
    /**
     * The Finish Import button click handler
     */
    onFinishImportClick() {
        return __awaiter(this, void 0, void 0, function* () {
            this.isLoading = true;
            const isSuccess = yield this.importStatusComponent.finishImport();
            if (isSuccess) {
                this.finishImport.emit();
            }
            this.isLoading = false;
        });
    }
    /**
     * Fired when the current step is changed
     * @param step - The current step
     */
    onCurrentStepChange(step) {
        this.currentStep = step;
        // Dismiss all alerts when step has been changed.
        this.alertService.dismissAll();
    }
    /**
     * Fired when Wizard is about to change to the next step
     *
     * @param event <BentoWizardComponentEvent>
     */
    onCurrentStepChanging(event) {
        return __awaiter(this, void 0, void 0, function* () {
            const proceedNext = new EventEmitter();
            const proceedToFinishStep = new EventEmitter();
            switch (event.step) {
                case 1: // Mapping step selected
                    // Prevents propagation to proceed to the next step
                    event.stopPropagation(proceedNext);
                    // If file selection is unsuccessful then keep user at the same step
                    if (!(yield this.selectFileComponent.selectFile())) {
                        break;
                    }
                    // Loads selected file
                    yield this.fileDataService.loadInitialDataAsync();
                    // If file parsing is unsuccessful then keep user at the same step
                    if (!this.fileDataService.isFileParsed) {
                        break;
                    }
                    // Switching to the next step
                    proceedNext.emit(true);
                    break;
                case 2: // Validation step selected
                    // Prevents propagation to proceed to the validation step
                    event.stopPropagation(proceedToFinishStep);
                    // Validates selected spreadsheet
                    this.validationService.validate(this.importOptions.mappingOptions);
                    // Switching to the next step
                    proceedToFinishStep.emit(true);
                    break;
                default:
                    break;
            }
        });
    }
}
ImportWizardComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportWizardComponent, deps: [{ token: ImportWizardService }, { token: SelectFileService }, { token: FileDataService }, { token: ColumnMappingService }, { token: ValidationService }, { token: AlertService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
ImportWizardComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: ImportWizardComponent, selector: "import-wizard", inputs: { type: "type", hideTitle: "hideTitle" }, outputs: { finishImport: "finishImport" }, providers: [
        FileDataService,
        SelectFileService,
        ImportContextService,
        ColumnMappingService,
        ValidationService,
        ValidationReportService,
        CsvDataService,
    ], viewQueries: [{ propertyName: "selectFileComponent", first: true, predicate: SelectFileComponent, descendants: true }, { propertyName: "importStatusComponent", first: true, predicate: ImportStatusComponent, descendants: true }], ngImport: i0, template: "<div *ngIf=\"!hideTitle\" class=\"container-fluid no-left-padding\">\n  <h1 class=\"no-top-margin\">{{ importTitle }}</h1>\n</div>\n<div class=\"container-fluid\" [bentoBusyLoader]=\"isLoading\" [size]=\"'lg'\">\n  <bento-wizard\n    [hTag]=\"'h1'\"\n    [hideDoneButton]=\"true\"\n    (currentStepChange)=\"onCurrentStepChange($event)\"\n    (currentStepChanging)=\"onCurrentStepChanging($event)\"\n    class=\"bento-wizard-import\"\n  >\n    <!-- Definition and content of a Bento Wizard -->\n    <bento-wizard-step [title]=\"'bm.import.wizard.step.selectFile' | translate\" class=\"bento-wizard-step\">\n      <select-file [options]=\"importOptions.selectFilesOptions\"></select-file>\n    </bento-wizard-step>\n    <bento-wizard-step\n      [title]=\"'bm.import.wizard.step.map' | translate\"\n      [disabled]=\"(isMapStepEnabled | async) === false\"\n      class=\"bento-wizard-step\"\n    >\n      <import-mapping\n        [mappingOptions]=\"importOptions.mappingOptions\"\n        [maxAllowedRowCount]=\"importOptions.selectFilesOptions.maxAllowedRowCount\"\n      ></import-mapping>\n    </bento-wizard-step>\n    <bento-wizard-step\n      [title]=\"'bm.import.wizard.step.validate' | translate\"\n      [disabled]=\"(isValidateStepEnabled | async) === false\"\n      class=\"bento-wizard-step\"\n    >\n      <import-validation></import-validation>\n    </bento-wizard-step>\n    <bento-wizard-step\n      [title]=\"'bm.import.wizard.step.import' | translate\"\n      [disabled]=\"(isImportStepEnabled | async) !== true\"\n      class=\"bento-wizard-step\"\n    >\n      <import-status *ngIf=\"currentStep === 3\" [statusOptions]=\"importOptions.statusOptions\"></import-status\n    ></bento-wizard-step>\n    <bento-wizard-custom-button-group>\n      <button class=\"btn btn-primary\" [hidden]=\"!isFinishImportButtonVisible()\" (click)=\"onFinishImportClick()\">\n        {{ \"bm.import.wizard.common.finishImport\" | translate }}\n      </button>\n    </bento-wizard-custom-button-group>\n  </bento-wizard>\n</div>\n<alert></alert>\n", styles: [".bento-wizard-import .bento-wizard-step{padding-top:25px!important}.bento-wizard-import .bento-wizard-contents,.bento-wizard-import .bento-wizard-contents .bento-wizard-step,.bento-wizard-import .bento-wizard-contents .wizard-step-content .container-fluid{padding-left:0!important;padding-right:0!important}\n"], components: [{ type: i2.BentoWizardComponent, selector: "bento-wizard", inputs: ["hideDefaultButtons", "hidePreviousButton", "hideNextButton", "hideDoneButton", "showCancelButton", "useStickyFooter", "stickyFooterFullWidth", "currentStep", "hTag"], outputs: ["currentStepChange", "currentStepChanging", "cancel", "done"] }, { type: i2.BentoWizardStepComponent, selector: "bento-wizard-step", inputs: ["title", "description", "disabled", "completed"] }, { type: SelectFileComponent, selector: "select-file", inputs: ["options"] }, { type: ImportMappingComponent, selector: "import-mapping", inputs: ["mappingOptions", "maxAllowedRowCount"] }, { type: ImportValidationComponent, selector: "import-validation" }, { type: ImportStatusComponent, selector: "import-status", inputs: ["statusOptions"] }, { type: i2.BentoWizardComponentCustomButtonGroupComponent, selector: "bento-wizard-custom-button-group" }, { type: AlertComponent, selector: "alert" }], directives: [{ type: i7.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.BentoBusyLoaderDirective, selector: "[bentoBusyLoader]", inputs: ["bentoBusyLoader", "size", "showDelay", "hideDelay"], outputs: ["show", "hide"] }, { type: i2.BentoFocusMonitorDirective, selector: "a, button.btn, button.close, button.dropdown-item, a.dropdown-item, button.bento-number-input-button, button.bento-toast-action-btn" }], pipes: { "translate": i1.TranslatePipe, "async": i7.AsyncPipe }, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportWizardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'import-wizard', encapsulation: ViewEncapsulation.None, providers: [
                        FileDataService,
                        SelectFileService,
                        ImportContextService,
                        ColumnMappingService,
                        ValidationService,
                        ValidationReportService,
                        CsvDataService,
                    ], template: "<div *ngIf=\"!hideTitle\" class=\"container-fluid no-left-padding\">\n  <h1 class=\"no-top-margin\">{{ importTitle }}</h1>\n</div>\n<div class=\"container-fluid\" [bentoBusyLoader]=\"isLoading\" [size]=\"'lg'\">\n  <bento-wizard\n    [hTag]=\"'h1'\"\n    [hideDoneButton]=\"true\"\n    (currentStepChange)=\"onCurrentStepChange($event)\"\n    (currentStepChanging)=\"onCurrentStepChanging($event)\"\n    class=\"bento-wizard-import\"\n  >\n    <!-- Definition and content of a Bento Wizard -->\n    <bento-wizard-step [title]=\"'bm.import.wizard.step.selectFile' | translate\" class=\"bento-wizard-step\">\n      <select-file [options]=\"importOptions.selectFilesOptions\"></select-file>\n    </bento-wizard-step>\n    <bento-wizard-step\n      [title]=\"'bm.import.wizard.step.map' | translate\"\n      [disabled]=\"(isMapStepEnabled | async) === false\"\n      class=\"bento-wizard-step\"\n    >\n      <import-mapping\n        [mappingOptions]=\"importOptions.mappingOptions\"\n        [maxAllowedRowCount]=\"importOptions.selectFilesOptions.maxAllowedRowCount\"\n      ></import-mapping>\n    </bento-wizard-step>\n    <bento-wizard-step\n      [title]=\"'bm.import.wizard.step.validate' | translate\"\n      [disabled]=\"(isValidateStepEnabled | async) === false\"\n      class=\"bento-wizard-step\"\n    >\n      <import-validation></import-validation>\n    </bento-wizard-step>\n    <bento-wizard-step\n      [title]=\"'bm.import.wizard.step.import' | translate\"\n      [disabled]=\"(isImportStepEnabled | async) !== true\"\n      class=\"bento-wizard-step\"\n    >\n      <import-status *ngIf=\"currentStep === 3\" [statusOptions]=\"importOptions.statusOptions\"></import-status\n    ></bento-wizard-step>\n    <bento-wizard-custom-button-group>\n      <button class=\"btn btn-primary\" [hidden]=\"!isFinishImportButtonVisible()\" (click)=\"onFinishImportClick()\">\n        {{ \"bm.import.wizard.common.finishImport\" | translate }}\n      </button>\n    </bento-wizard-custom-button-group>\n  </bento-wizard>\n</div>\n<alert></alert>\n", styles: [".bento-wizard-import .bento-wizard-step{padding-top:25px!important}.bento-wizard-import .bento-wizard-contents,.bento-wizard-import .bento-wizard-contents .bento-wizard-step,.bento-wizard-import .bento-wizard-contents .wizard-step-content .container-fluid{padding-left:0!important;padding-right:0!important}\n"] }]
        }], ctorParameters: function () { return [{ type: ImportWizardService }, { type: SelectFileService }, { type: FileDataService }, { type: ColumnMappingService }, { type: ValidationService }, { type: AlertService }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { type: [{
                type: Input
            }], hideTitle: [{
                type: Input
            }], finishImport: [{
                type: Output
            }], selectFileComponent: [{
                type: ViewChild,
                args: [SelectFileComponent]
            }], importStatusComponent: [{
                type: ViewChild,
                args: [ImportStatusComponent]
            }] } });

var StaticFilesAccessType;
(function (StaticFilesAccessType) {
    StaticFilesAccessType["Open"] = "Open";
    StaticFilesAccessType["Secure"] = "Secure";
    StaticFilesAccessType["Company"] = "Company";
})(StaticFilesAccessType || (StaticFilesAccessType = {}));
class StaticFilesService {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    /**
     * Creates or updates file in DMS Static Files Storage
     *
     * @param params <StaticFileParams>
     */
    createOrUpdateFile(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = this.getCreateOrUpdateFileUrl(params.accessType, params.application, params.fileName);
            const isTemporary = Boolean(params.isTemporary);
            const promise = this.httpClient
                .put(url, params.blob, {
                headers: { 'Content-Type': params.blob.type },
                params: { temp: isTemporary.toString() },
            })
                .toPromise();
            return promise;
        });
    }
    getCreateOrUpdateFileUrl(accessType, application, fileName) {
        return `/api/storage/v1/files/${accessType}/application/${application}/file/${fileName}`;
    }
}
StaticFilesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: StaticFilesService, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
StaticFilesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: StaticFilesService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: StaticFilesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }]; } });

class MigrationService {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    /**
     * Creates a new migration event.
     * @param event event data.
     */
    createMigrationEvent(event) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.httpClient
                .post(this.getPostUrl(), event, {
                observe: 'response',
            })
                .toPromise();
            const location = response.headers.get('location');
            const locationPaths = location.split('/');
            // Avoid trailing slash
            const migrationId = locationPaths.pop() || locationPaths.pop();
            return Promise.resolve(migrationId);
        });
    }
    getPostUrl() {
        return '/api/migrations/v2/migration-events';
    }
}
MigrationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: MigrationService, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
MigrationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: MigrationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: MigrationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }]; } });

let ImportStatusBase = class ImportStatusBase {
    constructor(locale, importContextService, staticFilesService, selectFileService, migrationService, alertService) {
        this.importContextService = importContextService;
        this.staticFilesService = staticFilesService;
        this.selectFileService = selectFileService;
        this.migrationService = migrationService;
        this.alertService = alertService;
        this.getCompanyId = getCompanyId;
        this.getContactId = getContactId;
        this.locale = locale;
    }
    getTotalRows() {
        return this.importContextService.getNumRows();
    }
    finishImport() {
        return __awaiter(this, void 0, void 0, function* () {
            this.alertService.dismissAll();
            const status = yield this.sendFileToStaticStorage();
            if (!status.isSuccess) {
                this.showServiceNotAvailableAlert();
                return false;
            }
            const migrationStatus = yield this.sendMigrationEvent(status.fileName);
            if (!migrationStatus.isSuccess) {
                this.showServiceNotAvailableAlert();
                return false;
            }
            // Log imported file name for testing purposes.
            console.log(`Imported file name: '${status.fileName}'`);
            // Log migration event id for testing purposes.
            console.log(`Migration event ID: '${migrationStatus.eventId}'`);
            return true;
        });
    }
    sendFileToStaticStorage() {
        return __awaiter(this, void 0, void 0, function* () {
            const selectedFile = this.selectFileService.selectedFiles[0];
            const fileExtension = selectedFile.name.split('.').pop();
            // Generate fileName with the following pattern: '{timestamp}-{random string}.{file extension}'
            const fileName = `${this.getRandomString()}.${fileExtension}`;
            try {
                yield this.staticFilesService.createOrUpdateFile({
                    accessType: StaticFilesAccessType.Company,
                    application: 'fm-excel-import',
                    fileName,
                    blob: selectedFile,
                    isTemporary: true,
                });
                return { isSuccess: true, fileName };
            }
            catch (_a) {
                return { isSuccess: false };
            }
        });
    }
    sendMigrationEvent(fileName) {
        return __awaiter(this, void 0, void 0, function* () {
            const companyId = yield this.getCompanyId();
            const creatorContactId = yield this.getContactId();
            const mappings = Array.from(this.importContextService.columnsMappings).map(entry => {
                return {
                    index: entry[0],
                    name: entry[1].name,
                };
            });
            const metadata = {
                skipFirstRow: this.importContextService.skipFirstRow,
                sheetName: this.importContextService.selectedSheet,
                ImportedFileName: this.selectFileService.selectedFiles[0].name,
                mappings,
                languageCode: this.locale,
            };
            const event = {
                companyId,
                creatorContactId,
                type: this.importType,
                itemId: this.getRandomString(),
                payload: fileName,
                statusMessage: JSON.stringify(metadata),
            };
            try {
                const eventId = yield this.migrationService.createMigrationEvent(event);
                return { isSuccess: true, eventId };
            }
            catch (_a) {
                return { isSuccess: false };
            }
        });
    }
    getRandomString() {
        const timestamp = new Date().getTime();
        const randStr = Math.random().toString(36).substr(2, 10);
        return `${timestamp}-${randStr}`;
    }
    showServiceNotAvailableAlert() {
        this.alertService.addAlert('danger', {
            key: 'bm.import.wizard.step.import.serviceNotAvailable',
        });
    }
};
ImportStatusBase = __decorate([
    __param(0, Inject(LOCALE_ID)),
    __metadata("design:paramtypes", [String, ImportContextService,
        StaticFilesService,
        SelectFileService,
        MigrationService,
        AlertService])
], ImportStatusBase);

class DecimalParser {
    constructor(locale) {
        this.locale = locale;
        const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
        this.thousandsSeparator = parts.find(d => d.type === 'group').value;
        if (this.thousandsSeparator === String.fromCharCode(160)) {
            this.thousandsSeparator = ' ';
        }
        this.decimalSeparator = parts.find(d => d.type === 'decimal').value;
        this.decimalFormat = new RegExp('^\\s*([+-]?(?:(?:\\d{1,3}(?:\\' +
            this.thousandsSeparator +
            '\\d{3,3})+)|\\d*))(?:\\' +
            this.decimalSeparator +
            '(\\d*))?\\s*$');
    }
    convertStringToDecimal(value) {
        if (isEmpty(value)) {
            return NaN;
        }
        const result = this.decimalFormat.exec(value.trim());
        if (!(result !== null && result.length === 3)) {
            return NaN;
        }
        const integerPart = result[1].replace(new RegExp('\\' + this.thousandsSeparator, 'g'), '');
        const fractionPart = result[2];
        const num = parseFloat(`${integerPart}.${fractionPart}`);
        return num;
    }
    convertCurrencyToDecimal(value) {
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value === 'string') {
            return this.convertStringToDecimal(value.replace(getLocaleCurrencySymbol(this.locale), ''));
        }
        return NaN;
    }
}

class WipStatusComponent extends ImportStatusBase {
    constructor(locale, importContextService, staticFilesService, selectFileService, migrationService, alertService) {
        super(locale, importContextService, staticFilesService, selectFileService, migrationService, alertService);
        this.totalRows = 0;
        this.totalWip = 0;
        this.locale = locale;
    }
    get importType() {
        return 'time-and-expense-excel-import';
    }
    ngOnInit() {
        this.totalRows = this.getTotalRows();
        this.totalWip = this.getTotalWip();
    }
    getTotalWip() {
        const sheetData = this.importContextService.sheetData;
        const skipFirstRow = this.importContextService.skipFirstRow;
        const columnsMappings = this.importContextService.columnsMappings;
        const wipColumnName = 'Amount';
        const wipColumnIndex = [...columnsMappings.keys()].find(key => columnsMappings.get(key).name === wipColumnName);
        let totalWip = 0;
        if (wipColumnIndex) {
            const decimalParser = new DecimalParser(this.locale);
            sheetData.forEach((item, index) => {
                if (index === 0 && skipFirstRow) {
                    return;
                }
                totalWip += decimalParser.convertCurrencyToDecimal(item[wipColumnIndex]);
            });
        }
        return totalWip;
    }
}
WipStatusComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: WipStatusComponent, deps: [{ token: LOCALE_ID }, { token: ImportContextService }, { token: StaticFilesService }, { token: SelectFileService }, { token: MigrationService }, { token: AlertService }], target: i0.ɵɵFactoryTarget.Component });
WipStatusComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: WipStatusComponent, selector: "wip-status", usesInheritance: true, ngImport: i0, template: "<div class=\"status-conatiner d-flex align-items-center\">\n  <div class=\"container\">\n    <div class=\"row justify-content-center\">\n      <div class=\"col-md-4\">{{ \"bm.import.wizard.step.import.totalWip\" | translate }}: {{ totalWip | currency }}</div>\n    </div>\n    <div class=\"row justify-content-center\">\n      <div class=\"col-md-4\">{{ \"bm.import.wizard.step.import.totalRows\" | translate }}: {{ totalRows | number }}</div>\n    </div>\n  </div>\n</div>\n", styles: [".status-conatiner{min-height:200px}\n"], pipes: { "translate": i1.TranslatePipe, "currency": i7.CurrencyPipe, "number": i7.DecimalPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: WipStatusComponent, decorators: [{
            type: Component,
            args: [{ selector: 'wip-status', template: "<div class=\"status-conatiner d-flex align-items-center\">\n  <div class=\"container\">\n    <div class=\"row justify-content-center\">\n      <div class=\"col-md-4\">{{ \"bm.import.wizard.step.import.totalWip\" | translate }}: {{ totalWip | currency }}</div>\n    </div>\n    <div class=\"row justify-content-center\">\n      <div class=\"col-md-4\">{{ \"bm.import.wizard.step.import.totalRows\" | translate }}: {{ totalRows | number }}</div>\n    </div>\n  </div>\n</div>\n", styles: [".status-conatiner{min-height:200px}\n"] }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [LOCALE_ID]
                    }] }, { type: ImportContextService }, { type: StaticFilesService }, { type: SelectFileService }, { type: MigrationService }, { type: AlertService }];
    } });

class LedgerStatusComponent extends ImportStatusBase {
    constructor(locale, importContextService, staticFilesService, selectFileService, migrationService, alertService, translateService) {
        super(locale, importContextService, staticFilesService, selectFileService, migrationService, alertService);
        this.translateService = translateService;
        this.invoices = [];
        this.receipts = [];
        // Total number of all rows to be imported
        this.totalImportedRows = 0;
        // Total number of all invoices rows to be imported
        this.importedInvoices = 0;
        // Total number of all receipts rows to be imported
        this.importedReceipts = 0;
        // Total Invoices values for NET, VAT and GROSS columns
        this.totalInvoices = { net: 0, vat: 0, gross: 0 };
        // Total Receipts values for NET, VAT and GROSS columns
        this.totalReceipt = { net: 0, vat: 0, gross: 0 };
        this.invoiceType = this.translateService.instant('bm.import.wizard.ledger.invoice');
        this.numberParser = new DecimalParser(locale);
    }
    get typeColumnName() {
        return 'Type';
    }
    get vatColumnName() {
        return 'Vat';
    }
    get netColumnName() {
        return 'Net';
    }
    get grossColumnName() {
        return 'Gross';
    }
    get invoiceNumberColumnName() {
        return 'InvoiceNumber';
    }
    get importType() {
        return 'accounts-receivable-excel-import';
    }
    ngOnInit() {
        this.processDocument();
        this.totalImportedRows = this.getTotalRows();
        this.importedInvoices = this.getImportedInvoices();
        this.importedReceipts = this.getImportedReceipts();
    }
    /**
     * Extracts receipts/invoices values
     */
    processDocument() {
        const sheetData = this.importContextService.sheetData;
        const skipFirstRow = this.importContextService.skipFirstRow;
        const columnsMappings = this.importContextService.columnsMappings;
        const typeColumnIndex = [...columnsMappings.keys()].find(key => columnsMappings.get(key).name === this.typeColumnName);
        if (typeColumnIndex !== null) {
            sheetData.forEach((item, index) => {
                if (index === 0 && skipFirstRow) {
                    return;
                }
                // Extract data if type is 'Invoice'
                if (item[typeColumnIndex].toString().toLowerCase() === this.invoiceType.toLowerCase()) {
                    const invoiceData = this.extractBillValues(item, columnsMappings);
                    this.addBill(this.invoices, this.totalInvoices, invoiceData);
                }
                // Extract data if type is 'Receipt'
                else {
                    const receiptData = this.extractBillValues(item, columnsMappings);
                    receiptData.net = 0;
                    receiptData.vat = 0; // NET and VAT for Receipts are always Zero
                    this.addBill(this.receipts, this.totalReceipt, receiptData);
                }
            });
        }
    }
    /**
     * Adds bill to storage and increments totals
     * @param dataStorage Invoice/Receipts storage
     * @param totalBill Invoice/Receipt totals for NET, VAT and GROSS columns
     * @param item Receipt/Invoice data
     */
    addBill(dataStorage, totalBill, item) {
        // Increments total bill values
        if (!isNaN(item.gross)) {
            totalBill.gross += item.gross;
        }
        if (!isNaN(item.net)) {
            totalBill.net += item.net;
        }
        if (!isNaN(item.vat)) {
            totalBill.vat += item.vat;
        }
        // Adds data to the specified storage
        dataStorage.push(item);
    }
    /**
     * Retrieves Receipt/Invoice data
     * @param rowItem Spreadsheet data row
     * @param columnsMappings Mapped columns
     */
    extractBillValues(rowItem, columnsMappings) {
        let invoiceNumberColumnIndex;
        let netColumnIndex;
        let vatColumnIndex;
        let grossColumnIndex;
        // Retrieves required columns indexes
        for (const [key, value] of columnsMappings) {
            switch (value.name) {
                case this.invoiceNumberColumnName:
                    invoiceNumberColumnIndex = key;
                    break;
                case this.netColumnName:
                    netColumnIndex = key;
                    break;
                case this.vatColumnName:
                    vatColumnIndex = key;
                    break;
                case this.grossColumnName:
                    grossColumnIndex = key;
                    break;
                default:
                    break;
            }
        }
        const bill = {
            number: invoiceNumberColumnIndex === null ? rowItem[invoiceNumberColumnIndex] : null,
            net: netColumnIndex === null ? 0 : this.numberParser.convertCurrencyToDecimal(rowItem[netColumnIndex]),
            vat: vatColumnIndex === null ? 0 : this.numberParser.convertCurrencyToDecimal(rowItem[vatColumnIndex]),
            gross: grossColumnIndex === null ? 0 : this.numberParser.convertCurrencyToDecimal(rowItem[grossColumnIndex]),
        };
        return bill;
    }
    /**
     * Total number of all different invoice numbers* to be imported
     */
    getImportedInvoices() {
        const uniqueInvoices = this.invoices.reduce((items, item) => items.find(x => item.number !== null && x.number === item.number) ? [...items] : [...items, item], []);
        return uniqueInvoices.length;
    }
    /**
     * Total number of all receipts rows to be imported
     */
    getImportedReceipts() {
        return this.receipts.length;
    }
}
LedgerStatusComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LedgerStatusComponent, deps: [{ token: LOCALE_ID }, { token: ImportContextService }, { token: StaticFilesService }, { token: SelectFileService }, { token: MigrationService }, { token: AlertService }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Component });
LedgerStatusComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: LedgerStatusComponent, selector: "ledger-status", usesInheritance: true, ngImport: i0, template: "<div class=\"status-conatiner\">\n  <div class=\"container p-3 my-3 border border-dark\">\n    <div class=\"table-responsive\">\n      <table class=\"table table-borderless\">\n        <tbody>\n          <tr>\n            <td></td>\n            <td class=\"text-right\" scope=\"col\">{{ \"bm.import.wizard.ledger.column.net\" | translate }}</td>\n            <td class=\"text-right\" scope=\"col\">{{ \"bm.import.wizard.ledger.column.vat\" | translate }}</td>\n            <td class=\"text-right\" scope=\"col\">{{ \"bm.import.wizard.ledger.column.gross\" | translate }}</td>\n          </tr>\n          <tr>\n            <td scope=\"row\">{{ \"bm.import.wizard.step.import.ledger.totalInvoices\" | translate }}:</td>\n            <td class=\"text-right\">{{ totalInvoices.net | currency }}</td>\n            <td class=\"text-right\">{{ totalInvoices.vat | currency }}</td>\n            <td class=\"text-right\">{{ totalInvoices.gross | currency }}</td>\n          </tr>\n          <tr>\n            <td scope=\"row\">{{ \"bm.import.wizard.step.import.ledger.totalReceipts\" | translate }}:</td>\n            <td class=\"text-right\">{{ totalReceipt.net | currency }}</td>\n            <td class=\"text-right\">{{ totalReceipt.vat | currency }}</td>\n            <td class=\"text-right\">{{ totalReceipt.gross * -1 | currency }}</td>\n          </tr>\n          <tr>\n            <td></td>\n            <td colspan=\"3\" scope=\"colgroup\" class=\"pt-3 border-bottom\"></td>\n          </tr>\n          <tr>\n            <td scope=\"row\">{{ \"bm.import.wizard.step.import.ledger.totals\" | translate }}</td>\n            <td class=\"text-right\">\n              <strong>{{ totalInvoices.net | currency }}</strong>\n            </td>\n            <td class=\"text-right\">\n              <strong>{{ totalInvoices.vat | currency }}</strong>\n            </td>\n            <td class=\"text-right\">\n              <strong> {{ totalInvoices.gross - totalReceipt.gross | currency }} </strong>\n            </td>\n          </tr>\n        </tbody>\n      </table>\n    </div>\n  </div>\n  <div class=\"container p-3 my-3 border border-dark\">\n    <div class=\"row align-items-end\">\n      <div class=\"col-md-6\">{{ \"bm.import.wizard.step.import.ledger.importedInvoices\" | translate }}:</div>\n      <div class=\"col-md-2 text-right\">{{ importedInvoices | number }}</div>\n    </div>\n    <div class=\"row align-items-end\">\n      <div class=\"col-md-6\">{{ \"bm.import.wizard.step.import.ledger.importedReceipts\" | translate }}:</div>\n      <div class=\"col-md-2 text-right\">{{ importedReceipts | number }}</div>\n    </div>\n    <div class=\"row align-items-end\">\n      <div class=\"col-md-6\">{{ \"bm.import.wizard.step.import.ledger.totalRows\" | translate }}:</div>\n      <div class=\"col-md-2 text-right\">{{ totalImportedRows | number }}</div>\n    </div>\n  </div>\n</div>\n", styles: [".status-conatiner{margin:auto;max-width:650px;min-height:200px;min-width:350px;width:40%}.table{border-top:0}.table th,.table td{padding:0%}.table-borderless tbody+tbody,.table-borderless td,.table-borderless th,.table-borderless thead th{border:0}.border-bottom{border-bottom:1px solid #000!important}\n"], pipes: { "translate": i1.TranslatePipe, "currency": i7.CurrencyPipe, "number": i7.DecimalPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LedgerStatusComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ledger-status', template: "<div class=\"status-conatiner\">\n  <div class=\"container p-3 my-3 border border-dark\">\n    <div class=\"table-responsive\">\n      <table class=\"table table-borderless\">\n        <tbody>\n          <tr>\n            <td></td>\n            <td class=\"text-right\" scope=\"col\">{{ \"bm.import.wizard.ledger.column.net\" | translate }}</td>\n            <td class=\"text-right\" scope=\"col\">{{ \"bm.import.wizard.ledger.column.vat\" | translate }}</td>\n            <td class=\"text-right\" scope=\"col\">{{ \"bm.import.wizard.ledger.column.gross\" | translate }}</td>\n          </tr>\n          <tr>\n            <td scope=\"row\">{{ \"bm.import.wizard.step.import.ledger.totalInvoices\" | translate }}:</td>\n            <td class=\"text-right\">{{ totalInvoices.net | currency }}</td>\n            <td class=\"text-right\">{{ totalInvoices.vat | currency }}</td>\n            <td class=\"text-right\">{{ totalInvoices.gross | currency }}</td>\n          </tr>\n          <tr>\n            <td scope=\"row\">{{ \"bm.import.wizard.step.import.ledger.totalReceipts\" | translate }}:</td>\n            <td class=\"text-right\">{{ totalReceipt.net | currency }}</td>\n            <td class=\"text-right\">{{ totalReceipt.vat | currency }}</td>\n            <td class=\"text-right\">{{ totalReceipt.gross * -1 | currency }}</td>\n          </tr>\n          <tr>\n            <td></td>\n            <td colspan=\"3\" scope=\"colgroup\" class=\"pt-3 border-bottom\"></td>\n          </tr>\n          <tr>\n            <td scope=\"row\">{{ \"bm.import.wizard.step.import.ledger.totals\" | translate }}</td>\n            <td class=\"text-right\">\n              <strong>{{ totalInvoices.net | currency }}</strong>\n            </td>\n            <td class=\"text-right\">\n              <strong>{{ totalInvoices.vat | currency }}</strong>\n            </td>\n            <td class=\"text-right\">\n              <strong> {{ totalInvoices.gross - totalReceipt.gross | currency }} </strong>\n            </td>\n          </tr>\n        </tbody>\n      </table>\n    </div>\n  </div>\n  <div class=\"container p-3 my-3 border border-dark\">\n    <div class=\"row align-items-end\">\n      <div class=\"col-md-6\">{{ \"bm.import.wizard.step.import.ledger.importedInvoices\" | translate }}:</div>\n      <div class=\"col-md-2 text-right\">{{ importedInvoices | number }}</div>\n    </div>\n    <div class=\"row align-items-end\">\n      <div class=\"col-md-6\">{{ \"bm.import.wizard.step.import.ledger.importedReceipts\" | translate }}:</div>\n      <div class=\"col-md-2 text-right\">{{ importedReceipts | number }}</div>\n    </div>\n    <div class=\"row align-items-end\">\n      <div class=\"col-md-6\">{{ \"bm.import.wizard.step.import.ledger.totalRows\" | translate }}:</div>\n      <div class=\"col-md-2 text-right\">{{ totalImportedRows | number }}</div>\n    </div>\n  </div>\n</div>\n", styles: [".status-conatiner{margin:auto;max-width:650px;min-height:200px;min-width:350px;width:40%}.table{border-top:0}.table th,.table td{padding:0%}.table-borderless tbody+tbody,.table-borderless td,.table-borderless th,.table-borderless thead th{border:0}.border-bottom{border-bottom:1px solid #000!important}\n"] }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [LOCALE_ID]
                    }] }, { type: ImportContextService }, { type: StaticFilesService }, { type: SelectFileService }, { type: MigrationService }, { type: AlertService }, { type: i1.TranslateService }];
    } });

var ImportType;
(function (ImportType) {
    ImportType["WIP"] = "wip";
    ImportType["Ledger"] = "ledger";
    ImportType["Client"] = "client";
})(ImportType || (ImportType = {}));

function getCurrentLocale() {
    const locale = cookie('BMLanguageCode');
    return /(en|es|fr|nl|pt).*/.exec(locale) ? locale : 'en-GB';
}
function getCurrencyCode() {
    let code;
    try {
        code = getLocaleCurrencyCode(getCurrentLocale());
    }
    catch (_a) {
        return 'GBP';
    }
    return code;
}

var ClientColumns;
(function (ClientColumns) {
    ClientColumns["ID"] = "ClientCode";
    ClientColumns["Name"] = "Name";
    ClientColumns["EntityType"] = "EntityType";
    ClientColumns["SSN"] = "SocialSecurityNumber";
    ClientColumns["EIN"] = "EmployerIdentificationNumber";
    ClientColumns["DBA"] = "DoingBusinessAs";
    ClientColumns["Nickname"] = "NickName";
    ClientColumns["Title"] = "JobTitle";
    ClientColumns["Company"] = "CompanyName";
    ClientColumns["DOB"] = "DateOfBirth";
    ClientColumns["Address"] = "Address";
    ClientColumns["Address2"] = "AdditionalAddressLine";
    ClientColumns["City"] = "City";
    ClientColumns["State"] = "State";
    ClientColumns["Zip"] = "ZIPCode";
    ClientColumns["County"] = "County";
    ClientColumns["PhoneNumber"] = "PhoneNumber";
    ClientColumns["Extension"] = "Extension";
    ClientColumns["Email"] = "EmailAddress";
    ClientColumns["StartDate"] = "StartDate";
})(ClientColumns || (ClientColumns = {}));

class ClientStatusComponent extends ImportStatusBase {
    constructor(locale, importContextService, staticFilesService, selectFileService, migrationService, alertService, translateService, columnMappingService) {
        super(locale, importContextService, staticFilesService, selectFileService, migrationService, alertService);
        this.translateService = translateService;
        this.columnMappingService = columnMappingService;
        this.locale = locale;
    }
    get importType() {
        return 'client-excel-import';
    }
    ngOnInit() {
        const finishImportStr = this.translateService.instant('bm.import.wizard.common.finishImport');
        this.noSummaryStr = this.translateService.instant('bm.import.wizard.clients.step.summary', {
            btnText: `"${finishImportStr}"`,
        });
        const data = this.importContextService.skipFirstRow
            ? this.importContextService.sheetData.slice(1)
            : this.importContextService.sheetData;
        this.totalClients = this.getTotalRows();
        this.entityTotals = this.getEntityTotals(data);
    }
    getEntityTotals(data) {
        var _a, _b, _c, _d, _e;
        const numCols = (_b = (_a = data[0]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
        if (numCols > 0) {
            const entityTypeColIdx = this.getEntityColIdx(numCols);
            if (entityTypeColIdx < 0 || entityTypeColIdx > numCols - 1) {
                return [];
            }
            const entityTotals = {};
            for (const row of data) {
                const entityType = (_d = (_c = row[entityTypeColIdx]) === null || _c === void 0 ? void 0 : _c.toLowerCase()) !== null && _d !== void 0 ? _d : null;
                if (entityType) {
                    entityTotals[entityType] = ((_e = entityTotals[entityType]) !== null && _e !== void 0 ? _e : 0) + 1;
                }
            }
            return Object.entries(entityTotals).sort(([e1], [e2]) => e1.toLowerCase().localeCompare(e2.toLowerCase()));
        }
        return [];
    }
    getEntityColIdx(numCols) {
        let entityTypeColIdx = -1;
        for (let i = 0; i < numCols; ++i) {
            const column = this.columnMappingService.getColumnMapping(i);
            if (column.name === ClientColumns.EntityType) {
                entityTypeColIdx = i;
                break;
            }
        }
        return entityTypeColIdx;
    }
}
ClientStatusComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ClientStatusComponent, deps: [{ token: LOCALE_ID }, { token: ImportContextService }, { token: StaticFilesService }, { token: SelectFileService }, { token: MigrationService }, { token: AlertService }, { token: i1.TranslateService }, { token: ColumnMappingService }], target: i0.ɵɵFactoryTarget.Component });
ClientStatusComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: ClientStatusComponent, selector: "client-status", usesInheritance: true, ngImport: i0, template: "<div class=\"summary-container\">\n  <span class=\"label\">Total Clients:</span>\n  <span class=\"val\">{{ totalClients }}</span>\n  <ng-container *ngFor=\"let row of entityTotals\" class=\"summary-row\">\n    <span class=\"label\">{{ row[0] }}:</span>\n    <span class=\"val\">{{ row[1] }}</span>\n  </ng-container>\n</div>\n", styles: [".summary-container{align-items:center;display:grid;gap:0 15px;grid-template-columns:repeat(2,auto);justify-content:center}.label{text-align:left;text-transform:capitalize}\n"], directives: [{ type: i7.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ClientStatusComponent, decorators: [{
            type: Component,
            args: [{ selector: 'client-status', template: "<div class=\"summary-container\">\n  <span class=\"label\">Total Clients:</span>\n  <span class=\"val\">{{ totalClients }}</span>\n  <ng-container *ngFor=\"let row of entityTotals\" class=\"summary-row\">\n    <span class=\"label\">{{ row[0] }}:</span>\n    <span class=\"val\">{{ row[1] }}</span>\n  </ng-container>\n</div>\n", styles: [".summary-container{align-items:center;display:grid;gap:0 15px;grid-template-columns:repeat(2,auto);justify-content:center}.label{text-align:left;text-transform:capitalize}\n"] }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [LOCALE_ID]
                    }] }, { type: ImportContextService }, { type: StaticFilesService }, { type: SelectFileService }, { type: MigrationService }, { type: AlertService }, { type: i1.TranslateService }, { type: ColumnMappingService }];
    } });

class StringMaxValueValidator extends ValueValidator {
    constructor(error, howToFix, maxLength) {
        super(error, howToFix, false);
        this.maxLength = maxLength;
    }
    isValid(value) {
        if (!isEmpty(value)) {
            const valueStr = value.toString();
            if (valueStr.length > this.maxLength) {
                return false;
            }
        }
        return true;
    }
}

class StringMinValueValidator extends ValueValidator {
    constructor(error, howToFix, minLength) {
        super(error, howToFix, false);
        this.minLength = minLength;
    }
    isValid(value) {
        if (!isEmpty(value)) {
            const valueStr = value.toString();
            if (valueStr.length < this.minLength) {
                return false;
            }
        }
        return true;
    }
}

class RegexValidator extends ValueValidator {
    constructor(error, howToFix, regex, required = false) {
        super(error, howToFix, false);
        this.regex = regex;
        this.required = required;
    }
    isValid(value) {
        if (value === null || value === undefined || value === '') {
            /*
              Can't validate something that doesn't exist, so only return error if this field was supposed to exist.
             */
            return !this.required;
        }
        return typeof value === 'string' && this.regex.test(value);
    }
}

class StringColumnOptions extends ColumnOptions {
    constructor(options) {
        super(options);
        if (!options.maxLengthError) {
            options.maxLengthError = options.translateService.instant('bm.import.wizard.validation.maxLengthError', {
                columnText: this.text,
            });
        }
        const maxLengthErrorHowToFix = options.translateService.instant('bm.import.wizard.validation.maxLengthErrorHowToFix', { columnText: this.text, maxLength: options.maxLength });
        this.validatorBuilder.addValidator(new StringMaxValueValidator(options.maxLengthError, maxLengthErrorHowToFix, options.maxLength));
        if (options.minLength) {
            if (!options.minLengthError) {
                options.minLengthError = options.translateService.instant('bm.import.wizard.validation.minLengthError', {
                    columnText: this.text,
                });
            }
            const minLengthErrorHowToFix = options.translateService.instant('bm.import.wizard.validation.minLengthErrorHowToFix', { columnText: this.text, minLength: options.minLength });
            this.validatorBuilder.addValidator(new StringMinValueValidator(options.minLengthError, minLengthErrorHowToFix, options.minLength));
        }
        if (options.regex) {
            if (!options.regexError) {
                options.regexError = options.translateService.instant('bm.import.wizard.validation.notRecognised', {
                    columnText: this.text,
                });
            }
            const regexErrorHowToFix = options.regexHowToFixError || options.regexError;
            this.validatorBuilder.addValidator(new RegexValidator(options.regexError, regexErrorHowToFix, options.regex, options.isMandatory));
        }
    }
}

class DateFormatValidator extends ValueValidator {
    constructor(error, howToFix, allowedDateFormats) {
        super(error, howToFix, false);
        this.allowedDateFormats = allowedDateFormats;
    }
    isValid(value) {
        if (value === null) {
            return true;
        }
        if (value instanceof Date) {
            return true;
        }
        const stringValue = value.toString();
        if (moment(stringValue, this.allowedDateFormats, true).isValid()) {
            return true;
        }
        return false;
    }
}

class DateColumnOptions extends ColumnOptions {
    constructor(options) {
        super(options);
        if (!options.invalidDateFormatError) {
            options.invalidDateFormatError = options.translateService.instant('bm.import.wizard.validation.notRecognised', {
                columnText: this.text,
            });
        }
        if (!options.allowedDateFormats) {
            const localLocale = moment();
            localLocale.locale(options.locale); // Set this instance to use selected locale
            const formatL = localLocale.localeData().longDateFormat('L'); // Returns format based on locale. (Month numerical, day, year)
            const formatYearlessL = formatL.replace('YYYY', 'YY'); // Also included a short year format (two digits only)
            options.allowedDateFormats = [formatL, formatYearlessL];
        }
        const invalidDateFormatErrorHowToFix = options.translateService.instant('bm.import.wizard.validation.acceptableDateFormats', { dateFormats: options.allowedDateFormats.join(', ') });
        this.validatorBuilder.addValidator(new DateFormatValidator(options.invalidDateFormatError, invalidDateFormatErrorHowToFix, options.allowedDateFormats));
    }
}

class ListValidator extends ValueValidator {
    constructor(error, howToFix, allowedValuesList) {
        super(error, howToFix, false);
        this.allowedValuesList = allowedValuesList;
    }
    // This validator is case insensitive. Should be improved if case sensitivity becomes required
    isValid(value) {
        if (value === null || value === undefined) {
            return true;
        }
        const valueStr = value.toString().trim().toLowerCase();
        const index = this.allowedValuesList.findIndex(item => item.toString().trim().toLowerCase() === valueStr);
        if (index < 0) {
            return false;
        }
        return true;
    }
}

class ListColumnOptions extends ColumnOptions {
    constructor(options) {
        super(options);
        if (!options.listRecogniseError) {
            options.listRecogniseError = options.translateService.instant('bm.import.wizard.validation.listRecogniseError');
        }
        const listRecogniseErrorHowToFix = options.translateService.instant('bm.import.wizard.validation.listRecogniseErrorHowToFix', { allowedValues: options.allowedValuesList.join('/') });
        this.validatorBuilder.addValidator(new ListValidator(options.listRecogniseError, listRecogniseErrorHowToFix, options.allowedValuesList));
    }
}

class CurrencyValidator extends ValueValidator {
    constructor(error, howToFix, locale, minValue, maxValue, decimalPlaces) {
        super(error, howToFix, false);
        this.minValue = minValue;
        this.maxValue = maxValue;
        this.decimalPlaces = decimalPlaces;
        this.decimalParser = new DecimalParser(locale);
    }
    // Validation includes min and max values
    isValid(value) {
        if (isEmpty(value)) {
            return true;
        }
        value = this.decimalParser.convertCurrencyToDecimal(value);
        if (isNaN(value)) {
            return false;
        }
        // Validate max allowed decimal places count
        if (this.decimalPlaces > 0 && countDecimals(value) > this.decimalPlaces) {
            return false;
        }
        // Validate  min and max values
        return isValueInRange(value, this.minValue, this.maxValue);
    }
}

class CurrencyColumnOptions extends ColumnOptions {
    constructor(options) {
        super(options);
        // If no override - default error message
        if (!options.currencyFormatErrorLocalizationKey) {
            options.currencyFormatErrorLocalizationKey = 'bm.import.wizard.validation.decimalNumberFormatError';
        }
        // If no override - default how to fix message
        if (!options.currencyFormatErrorHowToFixLocalizationKey) {
            options.currencyFormatErrorHowToFixLocalizationKey =
                'bm.import.wizard.validation.decimalNumberFormatErrorHowToFix';
        }
        const currencyFormatError = options.translateService.instant(options.currencyFormatErrorLocalizationKey, {
            columnText: this.text,
            decimalPlaces: options.decimalPlaces,
        });
        const currencyFormatErrorHowToFix = options.translateService.instant(options.currencyFormatErrorHowToFixLocalizationKey, { columnText: this.text, decimalPlaces: options.decimalPlaces });
        this.validatorBuilder.addValidator(new CurrencyValidator(currencyFormatError, currencyFormatErrorHowToFix, options.locale, options.minValue, options.maxValue, options.decimalPlaces));
    }
}

class DecimalValidator extends ValueValidator {
    constructor(error, howToFix, locale, decimalPlaces, minValue, maxValue) {
        super(error, howToFix, false);
        this.decimalPlaces = decimalPlaces;
        this.minValue = minValue;
        this.maxValue = maxValue;
        this.decimalParser = new DecimalParser(locale);
    }
    isValid(value) {
        if (isEmpty(value)) {
            return true;
        }
        value = this.decimalParser.convertStringToDecimal(value.toString());
        if (isNaN(value)) {
            return false;
        }
        // Validate max allowed decimal places count
        if (this.decimalPlaces > 0 && countDecimals(value) > this.decimalPlaces) {
            return false;
        }
        // Validate  min and max values
        return isValueInRange(value, this.minValue, this.maxValue);
    }
}

class DecimalColumnOptions extends ColumnOptions {
    constructor(options) {
        super(options);
        // If no override - default error message
        if (!options.decimalFormatErrorLocalizationKey) {
            options.decimalFormatErrorLocalizationKey = 'bm.import.wizard.validation.decimalNumberFormatError';
        }
        // If no override - default how to fix message
        if (!options.decimalFormatErrorHowToFixLocalizationKey) {
            options.decimalFormatErrorHowToFixLocalizationKey =
                'bm.import.wizard.validation.decimalRangeNumberFormatErrorHowToFix';
        }
        const decimalFormatError = options.translateService.instant(options.decimalFormatErrorLocalizationKey, {
            columnText: this.text,
            decimalPlaces: options.decimalPlaces,
        });
        const decimalFormatErrorHowToFix = options.translateService.instant(options.decimalFormatErrorHowToFixLocalizationKey, {
            columnText: this.text,
            decimalPlaces: options.decimalPlaces,
            minValue: options.minValue,
            maxValue: options.maxValue,
        });
        this.validatorBuilder.addValidator(new DecimalValidator(decimalFormatError, decimalFormatErrorHowToFix, options.locale, options.decimalPlaces, options.minValue, options.maxValue));
    }
}

class DisplayOptions {
    constructor(align = 'left', format = '', customValueConverter = null) {
        this.align = align;
        this.format = format;
        this.customValueConverter = customValueConverter;
    }
}

class CurrencyValueConverter {
    constructor(locale) {
        this.parser = new DecimalParser(locale);
    }
    convertValue(value) {
        return this.parser.convertCurrencyToDecimal(value);
    }
}

/**
 * Implements IImportOptions to be able to be registered into ImportWizardService
 */
class ImportWipService {
    constructor(locale, translateService) {
        this.translateService = translateService;
        this.locale = locale.toLowerCase();
    }
    get titleKey() {
        return 'bm.import.wizard.wip.title';
    }
    get statusOptions() {
        return {
            statusComponentType: WipStatusComponent,
        };
    }
    get importTitle() {
        return this.translateService.instant(this.titleKey);
    }
    get selectFilesOptions() {
        return {
            selectFilesFromDriveOptions: {
                accept: '.xlsx',
                allowMultiple: false,
                showSelectedFiles: true,
                showFileSize: true,
                appendOnSelection: false,
                disableDuringUpload: true,
                showProgressPercentage: true,
            },
            selectFilesFromDocumentsOptions: {
                accept: '.xlsx',
                primarySelectedDrawer: FileDrawer.Firm,
            },
            maxAllowedFileSize: { value: 1048576, text: '1 MB' },
            maxAllowedRowCount: { value: 1000, text: '1,000' },
        };
    }
    get mappingOptions() {
        return {
            allowAutomappingColumns: true,
            columnMappings: [
                new StringColumnOptions({
                    translateService: this.translateService,
                    name: 'ClientCode',
                    textLocalizationKey: 'bm.import.wizard.wip.column.clientId',
                    isMandatory: true,
                    maxLength: 20,
                }),
                new DateColumnOptions({
                    translateService: this.translateService,
                    name: 'EntryDate',
                    textLocalizationKey: 'bm.import.wizard.wip.column.date',
                    isMandatory: true,
                    displayOptions: new DisplayOptions('left'),
                    locale: this.locale,
                }),
                new ListColumnOptions({
                    translateService: this.translateService,
                    name: 'ActivityType',
                    textLocalizationKey: 'bm.import.wizard.wip.column.timeOrExpense',
                    isMandatory: true,
                    allowedValuesList: this.getAllowedActivityTypes(),
                }),
                new CurrencyColumnOptions({
                    translateService: this.translateService,
                    name: 'Amount',
                    textLocalizationKey: 'bm.import.wizard.wip.column.wipValue',
                    isMandatory: true,
                    decimalPlaces: 2,
                    minValue: -99999.99,
                    maxValue: 99999.99,
                    displayOptions: new DisplayOptions('right', 'c', new CurrencyValueConverter(this.locale)),
                    locale: this.locale,
                }),
                new StringColumnOptions({
                    translateService: this.translateService,
                    name: 'ActivityCode',
                    textLocalizationKey: 'bm.import.wizard.wip.column.activityID',
                    isMandatory: false,
                    maxLength: 20,
                }),
                new ColumnOptions({
                    translateService: this.translateService,
                    name: 'ClientName',
                    textLocalizationKey: 'bm.import.wizard.wip.column.clientName',
                    isMandatory: false,
                }),
                new DecimalColumnOptions({
                    translateService: this.translateService,
                    name: 'HoursUnits',
                    textLocalizationKey: 'bm.import.wizard.wip.column.hoursUnits',
                    isMandatory: false,
                    decimalPlaces: 2,
                    minValue: -99999.99,
                    maxValue: 99999.99,
                    displayOptions: new DisplayOptions('right', 'n2'),
                    locale: this.locale,
                }),
                new ColumnOptions({
                    translateService: this.translateService,
                    name: 'EmployeeRateDescription',
                    textLocalizationKey: 'bm.import.wizard.wip.column.rate',
                    isMandatory: false,
                }),
                new StringColumnOptions({
                    translateService: this.translateService,
                    name: 'EngagementCode',
                    textLocalizationKey: 'bm.import.wizard.wip.column.serviceID',
                    isMandatory: false,
                    maxLength: 20,
                }),
                new StringColumnOptions({
                    translateService: this.translateService,
                    name: 'EmployeeCode',
                    textLocalizationKey: 'bm.import.wizard.wip.column.staffID',
                    isMandatory: false,
                    maxLength: 20,
                }),
            ],
        };
    }
    getAllowedActivityTypes() {
        const allowedValues = [
            this.translateService.instant('bm.import.wizard.wip.activityType.time'),
            this.translateService.instant('bm.import.wizard.wip.activityType.expense'),
        ];
        return allowedValues;
    }
}
ImportWipService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportWipService, deps: [{ token: LOCALE_ID }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
ImportWipService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportWipService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportWipService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [LOCALE_ID]
                    }] }, { type: i1.TranslateService }];
    } });

var LedgerColumnName;
(function (LedgerColumnName) {
    LedgerColumnName["ClientId"] = "ClientID";
    LedgerColumnName["Date"] = "Date";
    LedgerColumnName["Gross"] = "Gross";
    LedgerColumnName["InvoiceNumber"] = "InvoiceNumber";
    LedgerColumnName["ServiceId"] = "ServiceID";
    LedgerColumnName["StaffId"] = "StaffID";
    LedgerColumnName["Type"] = "Type";
    LedgerColumnName["ActivityID"] = "ActivityID";
    LedgerColumnName["BankAccount"] = "BankAccount";
    LedgerColumnName["ClientName"] = "ClientName";
    LedgerColumnName["Comments"] = "Comments";
    LedgerColumnName["DescriptionNotes"] = "DescriptionNotes";
    LedgerColumnName["Net"] = "Net";
    LedgerColumnName["PaymentType"] = "PaymentType";
    LedgerColumnName["Reference"] = "Reference";
    LedgerColumnName["Vat"] = "Vat";
})(LedgerColumnName || (LedgerColumnName = {}));

class TypeRowValidator extends RowValidator {
    constructor(columnsMapping, translateService) {
        super(true);
        this.translateService = translateService;
        this.errorsMap = new Map();
        for (const [key, value] of columnsMapping) {
            switch (value.name) {
                case LedgerColumnName.Type:
                    this.typeColumnIndex = key;
                    break;
                case LedgerColumnName.BankAccount:
                    this.bankAccountColumnIndex = key;
                    this.bankAccountColumnTitle = value.text;
                    break;
                case LedgerColumnName.Net:
                    this.netColumnIndex = key;
                    this.netColumnTitle = value.text;
                    break;
                case LedgerColumnName.ServiceId:
                    this.serviceIdColumnIndex = key;
                    this.serviceIdColumnTitle = value.text;
                    break;
                case LedgerColumnName.StaffId:
                    this.staffIdColumnIndex = key;
                    this.staffIdColumnTitle = value.text;
                    break;
                default:
                    break;
            }
        }
        // Get column title if column was not mapped
        this.bankAccountColumnTitle =
            this.bankAccountColumnTitle || this.translateService.instant('bm.import.wizard.ledger.column.bankAccount');
        this.netColumnTitle = this.netColumnTitle || this.translateService.instant('bm.import.wizard.ledger.column.net');
        this.serviceIdColumnTitle =
            this.serviceIdColumnTitle || this.translateService.instant('bm.import.wizard.ledger.column.serviceID');
        this.staffIdColumnTitle =
            this.staffIdColumnTitle || this.translateService.instant('bm.import.wizard.ledger.column.staffID');
        this.invoiceType = this.translateService.instant('bm.import.wizard.ledger.invoice');
        this.receiptType = this.translateService.instant('bm.import.wizard.ledger.receipt');
    }
    validateSpecifically(value) {
        const typeValue = value[this.typeColumnIndex];
        if (typeValue) {
            const validationResult = new Map();
            this.validateColumn(value, typeValue, this.receiptType, this.bankAccountColumnIndex, this.bankAccountColumnTitle, validationResult);
            this.validateColumn(value, typeValue, this.invoiceType, this.netColumnIndex, this.netColumnTitle, validationResult);
            this.validateColumn(value, typeValue, this.invoiceType, this.serviceIdColumnIndex, this.serviceIdColumnTitle, validationResult);
            this.validateColumn(value, typeValue, this.invoiceType, this.staffIdColumnIndex, this.staffIdColumnTitle, validationResult);
            return validationResult.size === 0 ? null : validationResult;
        }
        return null;
    }
    validateColumn(value, type, requiredType, columnIndex, columnTitle, validationResult) {
        const columnValue = value[columnIndex];
        if (type.toString().toUpperCase() === requiredType.toUpperCase()) {
            if (!columnIndex) {
                this.setErrorMessage(this.typeColumnIndex, columnTitle, 'bm.import.wizard.validation.notMappedError', requiredType, validationResult);
            }
            else if (isEmpty(columnValue)) {
                this.setErrorMessage(columnIndex, columnTitle, 'bm.import.wizard.validation.mandatoryError', requiredType, validationResult);
            }
        }
    }
    setErrorMessage(columnIndex, columnTitle, errorKey, requiredType, validationResult) {
        const error = this.getMessageText(columnTitle, errorKey);
        const howToFix = this.getMessageText(columnTitle, 'bm.import.wizard.validation.typeErrorHowToFix', requiredType);
        this.addValidationError(columnIndex, error, howToFix, validationResult);
    }
    getMessageText(columnName, errorKey, typeValue) {
        let errorMessage = this.errorsMap.get(errorKey + columnName);
        if (!errorMessage) {
            errorMessage = this.translateService.instant(errorKey, { columnText: columnName, type: typeValue });
            this.errorsMap.set(errorKey + columnName, errorMessage);
        }
        return errorMessage;
    }
}

class NumberValidator extends ValueValidator {
    constructor(error, howToFix, integerFormatRegExp, minValue, maxValue) {
        super(error, howToFix, false);
        this.minValue = minValue;
        this.maxValue = maxValue;
        this.regexp = new RegExp(integerFormatRegExp);
    }
    // Validation includes min and max values
    isValid(value) {
        if (isEmpty(value)) {
            return true;
        }
        if (this.regexp.test(value.toString().trim())) {
            if (typeof value !== 'number') {
                value = Number(value);
            }
            return isValueInRange(value, this.minValue, this.maxValue);
        }
        return false;
    }
}

class IntegerColumnOptions extends ColumnOptions {
    constructor(options) {
        super(options);
        if (!options.integerFormatError) {
            options.integerFormatError = options.translateService.instant('bm.import.wizard.validation.notRecognised', {
                columnText: this.text,
            });
        }
        if (!options.integerFormatRegExp) {
            options.integerFormatRegExp = '^([1-9]\\d*)$'; // Only positive integers with no leading zero
        }
        if (!options.integerFormatErrorHowToFix) {
            options.integerFormatErrorHowToFix = options.translateService.instant(options.translateService.instant('bm.import.wizard.validation.integerFormatErrorHowToFix', {
                columnText: this.text,
            }));
        }
        this.validatorBuilder.addValidator(new NumberValidator(options.integerFormatError, options.integerFormatErrorHowToFix, options.integerFormatRegExp, options.minValue, options.maxValue));
    }
}

class GrossRowValidator extends RowValidator {
    constructor(locale, columnsMapping, translateService) {
        super(true);
        this.locale = locale;
        this.translateService = translateService;
        this.setColumnIndexes(columnsMapping);
        this.invoiceType = this.translateService.instant('bm.import.wizard.ledger.invoice');
    }
    validateSpecifically(row) {
        const typeValue = String(row[this.typeColumnIndex]);
        if (!typeValue || typeValue.toUpperCase() !== this.invoiceType.toUpperCase()) {
            return null;
        }
        const validationResult = new Map();
        if (this.grossColumnIndex === null) {
            return null;
        }
        const decimalParser = new DecimalParser(this.locale);
        const grossValue = decimalParser.convertCurrencyToDecimal(row[this.grossColumnIndex]);
        const netValue = this.netColumnIndex === null ? NaN : decimalParser.convertCurrencyToDecimal(row[this.netColumnIndex]);
        const vatValue = this.vatColumnIndex === null ? 0 : decimalParser.convertCurrencyToDecimal(row[this.vatColumnIndex] || 0);
        const grossCalculation = (net, vat) => {
            return Number((net + vat).toFixed(2));
        };
        if (grossValue === grossCalculation(netValue, vatValue)) {
            return null;
        }
        const error = this.translateService.instant('bm.import.wizard.validation.grossSumError');
        const howToFix = this.translateService.instant('bm.import.wizard.validation.grossSumErrorHowToFix');
        this.addValidationError(this.grossColumnIndex, error, howToFix, validationResult);
        return validationResult.size === 0 ? null : validationResult;
    }
    setColumnIndexes(columnsMapping) {
        for (const [key, value] of columnsMapping) {
            switch (value.name) {
                case LedgerColumnName.Type:
                    this.typeColumnIndex = key;
                    break;
                case LedgerColumnName.Net:
                    this.netColumnIndex = key;
                    break;
                case LedgerColumnName.Gross:
                    this.grossColumnIndex = key;
                    break;
                case LedgerColumnName.Vat:
                    this.vatColumnIndex = key;
                    break;
                default:
                    break;
            }
        }
    }
}

/**
 * Implements IImportOptions to be able to be registered into ImportWizardService
 */
class ImportLedgerService {
    constructor(locale, translateService) {
        this.translateService = translateService;
        this.locale = locale.toLowerCase();
    }
    get titleKey() {
        return 'bm.import.wizard.ledger.title';
    }
    get statusOptions() {
        return {
            statusComponentType: LedgerStatusComponent,
        };
    }
    get importTitle() {
        return this.translateService.instant(this.titleKey);
    }
    get selectFilesOptions() {
        return {
            selectFilesFromDriveOptions: {
                accept: '.xlsx',
                allowMultiple: false,
                showSelectedFiles: true,
                showFileSize: true,
                appendOnSelection: false,
                disableDuringUpload: true,
                showProgressPercentage: true,
            },
            selectFilesFromDocumentsOptions: {
                accept: '.xlsx',
                primarySelectedDrawer: FileDrawer.Firm,
            },
            maxAllowedFileSize: { value: 1048576, text: '1 MB' },
            maxAllowedRowCount: { value: 1000, text: '1,000' },
        };
    }
    get mappingOptions() {
        return {
            allowAutomappingColumns: true,
            columnMappings: [
                new StringColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.ClientId,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.clientId',
                    isMandatory: true,
                    maxLength: 20,
                }),
                new DateColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.Date,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.date',
                    isMandatory: true,
                    locale: this.locale,
                }),
                new CurrencyColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.Gross,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.gross',
                    isMandatory: true,
                    decimalPlaces: 2,
                    minValue: 0,
                    maxValue: Number.MAX_VALUE,
                    currencyFormatErrorLocalizationKey: 'bm.import.wizard.validation.postiveDecimalNumberFormatError',
                    currencyFormatErrorHowToFixLocalizationKey: 'bm.import.wizard.validation.postiveDecimalNumberFormatErrorHowToFix',
                    displayOptions: new DisplayOptions('right', 'c'),
                    locale: this.locale,
                }),
                new IntegerColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.InvoiceNumber,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.invoiceNumber',
                    isMandatory: false,
                }),
                new StringColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.ServiceId,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.serviceID',
                    isMandatory: false,
                    maxLength: 20,
                }),
                new StringColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.StaffId,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.staffID',
                    isMandatory: false,
                    maxLength: 20,
                }),
                new ListColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.Type,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.type',
                    isMandatory: true,
                    allowedValuesList: this.getAllowedTypes(),
                }),
                new StringColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.ActivityID,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.activityID',
                    isMandatory: false,
                    maxLength: 20,
                }),
                new StringColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.BankAccount,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.bankAccount',
                    isMandatory: false,
                    maxLength: 20,
                }),
                new ColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.ClientName,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.clientName',
                    isMandatory: false,
                }),
                new ColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.Comments,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.comments',
                    isMandatory: false,
                }),
                new ColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.DescriptionNotes,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.descriptionNotes',
                    isMandatory: false,
                }),
                new CurrencyColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.Net,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.net',
                    isMandatory: false,
                    decimalPlaces: 2,
                    minValue: 0,
                    maxValue: Number.MAX_VALUE,
                    currencyFormatErrorLocalizationKey: 'bm.import.wizard.validation.postiveDecimalNumberFormatError',
                    currencyFormatErrorHowToFixLocalizationKey: 'bm.import.wizard.validation.postiveDecimalNumberFormatErrorHowToFix',
                    displayOptions: new DisplayOptions('right', 'c'),
                    locale: this.locale,
                }),
                new ColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.PaymentType,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.paymentType',
                    isMandatory: false,
                }),
                new StringColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.Reference,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.reference',
                    isMandatory: false,
                    maxLength: 30,
                }),
                new CurrencyColumnOptions({
                    translateService: this.translateService,
                    name: LedgerColumnName.Vat,
                    textLocalizationKey: 'bm.import.wizard.ledger.column.vat',
                    isMandatory: false,
                    decimalPlaces: 2,
                    minValue: 0,
                    maxValue: Number.MAX_VALUE,
                    currencyFormatErrorLocalizationKey: 'bm.import.wizard.validation.postiveDecimalNumberFormatError',
                    currencyFormatErrorHowToFixLocalizationKey: 'bm.import.wizard.validation.postiveDecimalNumberFormatErrorHowToFix',
                    displayOptions: new DisplayOptions('right', 'c'),
                    locale: this.locale,
                }),
            ],
            getRowValidators: (columnsMapping) => {
                const rowValidators = [
                    new TypeRowValidator(columnsMapping, this.translateService),
                    new GrossRowValidator(this.locale, columnsMapping, this.translateService),
                ];
                return rowValidators;
            },
        };
    }
    getAllowedTypes() {
        const allowedValues = [
            this.translateService.instant('bm.import.wizard.ledger.invoice'),
            this.translateService.instant('bm.import.wizard.ledger.receipt'),
        ];
        return allowedValues;
    }
}
ImportLedgerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportLedgerService, deps: [{ token: LOCALE_ID }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
ImportLedgerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportLedgerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportLedgerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [LOCALE_ID]
                    }] }, { type: i1.TranslateService }];
    } });

// https://github.com/angular/angular/blob/master/packages/forms/src/validators.ts#L98
const EMAIL_REGEX = /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
var EntityTypeId;
(function (EntityTypeId) {
    EntityTypeId["USORGANIZATION"] = "USORGANIZATION";
    EntityTypeId["USCCORPORATION"] = "USCCORPORATION";
    EntityTypeId["USESTATE"] = "USESTATE";
    EntityTypeId["USLLC"] = "USLLC";
    EntityTypeId["USPARTNERSHIP"] = "USPARTNERSHIP";
    EntityTypeId["USSCORPORATION"] = "USSCORPORATION";
    EntityTypeId["USSOLEPROPRIETOR"] = "USSOLEPROPRIETOR";
    EntityTypeId["USPRIVATEFOUNDATION"] = "USPRIVATEFOUNDATION";
    EntityTypeId["USEXEMPTORG"] = "USEXEMPTORG";
    EntityTypeId["USTRUST"] = "USTRUST";
    EntityTypeId["USUNSPECIFIED"] = "USUNSPECIFIED";
    EntityTypeId["USINDIVIDUAL"] = "USINDIVIDUAL";
    EntityTypeId["USOFFICE"] = "USOFFICE";
})(EntityTypeId || (EntityTypeId = {}));
class USEntityTypeRowValidator extends RowValidator {
    constructor(
    /* Maps column index -> column data */
    columnsMapping, translateService, entityTypes) {
        super(true);
        this.columnsMapping = columnsMapping;
        this.translateService = translateService;
        /* Maps entity type ID -> entity type Data */
        this.entityTypeMap = {};
        /* Maps column ID (name) -> column index */
        this.columnsMap = {};
        for (const entityType of entityTypes) {
            this.entityTypeMap[entityType.id] = entityType;
        }
        for (const [index, colMapping] of this.columnsMapping.entries()) {
            this.columnsMap[colMapping.name] = index;
        }
    }
    /* Validate a client's entity type */
    validateSpecifically(rowData) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        const errors = new Map();
        const entityTypeIdx = this.columnsMap[ClientColumns.EntityType];
        const entityType = rowData[entityTypeIdx];
        const einIdx = (_a = this.columnsMap[ClientColumns.EIN]) !== null && _a !== void 0 ? _a : -1;
        console.log(einIdx);
        const einText = (_b = this.columnsMapping.get(einIdx)) === null || _b === void 0 ? void 0 : _b.text;
        const ein = rowData[einIdx];
        const dbaIdx = (_c = this.columnsMap[ClientColumns.DBA]) !== null && _c !== void 0 ? _c : -1;
        const dbaText = (_d = this.columnsMapping.get(dbaIdx)) === null || _d === void 0 ? void 0 : _d.text;
        const dba = rowData[dbaIdx];
        const nicknameIdx = (_e = this.columnsMap[ClientColumns.Nickname]) !== null && _e !== void 0 ? _e : -1;
        const nicknameText = (_f = this.columnsMapping.get(nicknameIdx)) === null || _f === void 0 ? void 0 : _f.text;
        const nickname = rowData[nicknameIdx];
        const titleIdx = (_g = this.columnsMap[ClientColumns.Title]) !== null && _g !== void 0 ? _g : -1;
        const titleText = (_h = this.columnsMapping.get(titleIdx)) === null || _h === void 0 ? void 0 : _h.text;
        const title = rowData[titleIdx];
        const companyIdx = (_j = this.columnsMap[ClientColumns.Company]) !== null && _j !== void 0 ? _j : -1;
        const companyText = (_k = this.columnsMapping.get(companyIdx)) === null || _k === void 0 ? void 0 : _k.text;
        const company = rowData[companyIdx];
        const dobIdx = (_l = this.columnsMap[ClientColumns.DOB]) !== null && _l !== void 0 ? _l : -1;
        const dobText = (_m = this.columnsMapping.get(dobIdx)) === null || _m === void 0 ? void 0 : _m.text;
        const dob = rowData[dobIdx];
        const ssnIdx = (_o = this.columnsMap[ClientColumns.SSN]) !== null && _o !== void 0 ? _o : -1;
        const ssnText = (_p = this.columnsMapping.get(ssnIdx)) === null || _p === void 0 ? void 0 : _p.text;
        const ssn = rowData[ssnIdx];
        const isBusiness = this.isBusiness(entityType);
        const isIndividual = this.isIndividual(entityType);
        console.assert((isIndividual && !isBusiness) || (!isIndividual && isBusiness) || (!isIndividual && !isBusiness), 'Cannot be both Individual and Business');
        const msg = this.translateService.instant('bm.import.wizard.validation.entityType');
        const t8 = (col, biz) => biz
            ? this.translateService.instant('bm.import.wizard.validation.entityType.business.howToFix', { column: col })
            : this.translateService.instant('bm.import.wizard.validation.entityType.individual.howToFix', { column: col });
        if (ein && !isBusiness) {
            errors.set(einIdx, { error: [msg], howToFix: [t8(einText, isBusiness)] });
        }
        if (dba && !isBusiness) {
            errors.set(dbaIdx, { error: [msg], howToFix: [t8(dbaText, isBusiness)] });
        }
        if (nickname && !isIndividual) {
            errors.set(nicknameIdx, { error: [msg], howToFix: [t8(nicknameText, isBusiness)] });
        }
        if (title && !isIndividual) {
            errors.set(titleIdx, { error: [msg], howToFix: [t8(titleText, isBusiness)] });
        }
        if (company && !isIndividual) {
            errors.set(companyIdx, { error: [msg], howToFix: [t8(companyText, isBusiness)] });
        }
        if (dob && !isIndividual) {
            errors.set(dobIdx, { error: [msg], howToFix: [t8(dobText, isBusiness)] });
        }
        if (ssn && !isIndividual) {
            errors.set(ssnIdx, { error: [msg], howToFix: [t8(ssnText, isBusiness)] });
        }
        return errors.size === 0 ? null : errors;
    }
    isBusiness(entityType) {
        return (entityType &&
            !(entityType.toString().trim().toLowerCase() ===
                this.entityTypeMap[EntityTypeId.USINDIVIDUAL].name.toLowerCase() ||
                entityType.toString().trim().toLowerCase() === this.entityTypeMap[EntityTypeId.USUNSPECIFIED].name.toLowerCase()));
    }
    isIndividual(entityType) {
        return (entityType &&
            entityType.toString().trim().toLowerCase() === this.entityTypeMap[EntityTypeId.USINDIVIDUAL].name.toLowerCase());
    }
}
class DuplicateClientIdValidator extends FileValidator {
    constructor(
    /* Maps column index -> column data */
    columnsMapping, translateService) {
        super(true);
        this.columnsMapping = columnsMapping;
        this.translateService = translateService;
        /* Maps column ID (name) -> column index */
        this.columnsMap = {};
        for (const [index, colMapping] of this.columnsMapping.entries()) {
            this.columnsMap[colMapping.name] = index;
        }
    }
    validateSpecifically(fileData) {
        const errors = new Map();
        // Index where client id is in each row
        const clientIdIdx = this.columnsMap[ClientColumns.ID];
        const clientIdColumnOptions = this.columnsMapping.get(clientIdIdx);
        const clientIdHeaderText = clientIdColumnOptions.text;
        const error = this.translateService.instant('bm.import.wizard.validation.duplicate', {
            columnText: clientIdHeaderText,
        });
        const howToFix = this.translateService.instant('bm.import.wizard.validation.duplicate.howToFix', {
            columnText: clientIdHeaderText,
        });
        // Map of client ids -> [row, col]
        const clientIdsMap = {};
        // Fill map
        for (let rowIdx = 0; rowIdx < fileData.length; ++rowIdx) {
            const row = fileData[rowIdx];
            const clientId = row[clientIdIdx];
            clientIdsMap[clientId] = clientIdsMap[clientId] || [];
            clientIdsMap[clientId].push([rowIdx, clientIdIdx]);
        }
        // Check map for dupe client ids
        for (const [, clients] of Object.entries(clientIdsMap)) {
            if (clients.length > 1) {
                for (const [rowIdx, colIdx] of clients) {
                    errors.set(rowIdx, errors.get(rowIdx) || new Map());
                    const rowErrors = errors.get(rowIdx);
                    rowErrors.set(colIdx, rowErrors.get(colIdx) || { error: [], howToFix: [] });
                    const colErrors = rowErrors.get(colIdx);
                    colErrors.error.push(error);
                    colErrors.howToFix.push(howToFix);
                }
            }
        }
        return errors.size === 0 ? null : errors;
    }
}
class ImportClientService {
    constructor(locale, translateService) {
        this.locale = locale;
        this.translateService = translateService;
        this.entityTypesCache = {};
        /** Initialialize _mappingOptions asynchronously in initialize method */
        this.mappingOptionsLocal = null;
        this.locale = this.locale.toLowerCase();
    }
    get titleKey() {
        return 'bm.import.wizard.clients.title';
    }
    get importTitle() {
        return this.translateService.instant(this.titleKey);
    }
    get selectFilesOptions() {
        return {
            selectFilesFromDriveOptions: {
                accept: '.xlsx',
                allowMultiple: false,
                showSelectedFiles: true,
                showFileSize: true,
                appendOnSelection: false,
                disableDuringUpload: true,
                showProgressPercentage: true,
            },
            selectFilesFromDocumentsOptions: {
                accept: '.xlsx',
                primarySelectedDrawer: FileDrawer.Firm,
            },
            maxAllowedFileSize: { value: 1048576, text: '1 MB' },
            maxAllowedRowCount: { value: 1000, text: '1,000' },
        };
    }
    get mappingOptions() {
        return this.mappingOptionsLocal;
    }
    get statusOptions() {
        return {
            statusComponentType: ClientStatusComponent,
        };
    }
    initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            const ngModalSelector = '#bentoNGModalContainer';
            const hasNgModalContainer = Boolean(document.querySelector(ngModalSelector));
            const entityTypes = yield this.getAllEntityTypes();
            this.mappingOptionsLocal = {
                allowAutomappingColumns: true,
                columnMappings: yield this.getColumnOptions(),
                columnMappingModalContainer: hasNgModalContainer ? ngModalSelector : 'body',
                getRowValidators: columnsMapping => [
                    new USEntityTypeRowValidator(columnsMapping, this.translateService, entityTypes),
                ],
                getFileValidators: columnsMapping => [new DuplicateClientIdValidator(columnsMapping, this.translateService)],
            };
        });
    }
    getColumnOptions() {
        return __awaiter(this, void 0, void 0, function* () {
            /**
             * Todo: determine region using countryCode from Environments or IACs when other regions are added
             * todo: create functions to get region-agnostic columns when other regiosn are added
             */
            switch (this.locale) {
                case 'en-us': {
                    return this.getUSColumnOptions();
                }
                default: {
                    return this.getUSColumnOptions();
                }
            }
        });
    }
    getUSColumnOptions() {
        return __awaiter(this, void 0, void 0, function* () {
            return [
                new StringColumnOptions({
                    name: ClientColumns.ID,
                    isMandatory: true,
                    maxLength: 20,
                    textLocalizationKey: 'bm.import.wizard.clients.column.clientId',
                    translateService: this.translateService,
                    matcher: new ColumnMatcher({
                        translateService: this.translateService,
                        synonyms: ['bm.import.wizard.clients.column.clientId.id'],
                    }),
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.Name,
                    isMandatory: true,
                    maxLength: 100,
                    textLocalizationKey: 'bm.import.wizard.clients.column.clientName',
                    translateService: this.translateService,
                    matcher: new ColumnMatcher({
                        translateService: this.translateService,
                        synonyms: ['bm.import.wizard.clients.column.clientName.name'],
                    }),
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new ListColumnOptions({
                    name: ClientColumns.EntityType,
                    isMandatory: true,
                    allowedValuesList: yield this.getAllowedEntityTypeNames(),
                    textLocalizationKey: 'bm.import.wizard.clients.column.entityType',
                    translateService: this.translateService,
                    matcher: new ColumnMatcher({
                        translateService: this.translateService,
                        synonyms: ['bm.import.wizard.clients.column.entityType.entity'],
                    }),
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.SSN,
                    isMandatory: false,
                    maxLength: 100,
                    textLocalizationKey: 'bm.import.wizard.clients.column.einSsn.ssn',
                    translateService: this.translateService,
                    matcher: new ColumnMatcher({
                        translateService: this.translateService,
                    }),
                    regex: this.combineRegexes([/\d{3}-\d{2}-\d{4}/, 'Applied for', 'Foreign', 'Amish', 'Died']),
                    regexHowToFixError: this.translateService.instant('bm.import.wizard.validation.ssn.howtofix'),
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.EIN,
                    isMandatory: false,
                    maxLength: 100,
                    textLocalizationKey: 'bm.import.wizard.clients.column.einSsn.ein',
                    translateService: this.translateService,
                    matcher: new ColumnMatcher({
                        translateService: this.translateService,
                    }),
                    regex: this.combineRegexes([/\d{2}-\d{7}/, 'Applied for', 'Foreign']),
                    regexHowToFixError: this.translateService.instant('bm.import.wizard.validation.ein.howtofix'),
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.DBA,
                    isMandatory: false,
                    maxLength: 250,
                    textLocalizationKey: 'bm.import.wizard.clients.column.dbaNickname.doingBusinessAs',
                    translateService: this.translateService,
                    matcher: new ColumnMatcher({
                        translateService: this.translateService,
                        synonyms: ['bm.import.wizard.clients.column.dbaNickname.dba'],
                    }),
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.Nickname,
                    isMandatory: false,
                    maxLength: 250,
                    textLocalizationKey: 'bm.import.wizard.clients.column.dbaNickname.nickname',
                    translateService: this.translateService,
                    matcher: new ColumnMatcher({
                        translateService: this.translateService,
                    }),
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.Title,
                    isMandatory: false,
                    maxLength: 250,
                    textLocalizationKey: 'bm.import.wizard.clients.column.title',
                    translateService: this.translateService,
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.Company,
                    isMandatory: false,
                    maxLength: 250,
                    textLocalizationKey: 'bm.import.wizard.clients.column.company',
                    translateService: this.translateService,
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new DateColumnOptions({
                    name: ClientColumns.DOB,
                    isMandatory: false,
                    locale: this.locale,
                    textLocalizationKey: 'bm.import.wizard.clients.column.dob',
                    translateService: this.translateService,
                    matcher: new ColumnMatcher({
                        translateService: this.translateService,
                        synonyms: ['bm.import.wizard.clients.column.dob.birthday'],
                    }),
                }),
                new StringColumnOptions({
                    name: ClientColumns.Address,
                    isMandatory: false,
                    maxLength: 250,
                    textLocalizationKey: 'bm.import.wizard.clients.column.address',
                    translateService: this.translateService,
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.Address2,
                    isMandatory: false,
                    maxLength: 250,
                    textLocalizationKey: 'bm.import.wizard.clients.column.address2',
                    translateService: this.translateService,
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.City,
                    isMandatory: false,
                    maxLength: 100,
                    textLocalizationKey: 'bm.import.wizard.clients.column.city',
                    translateService: this.translateService,
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.State,
                    isMandatory: false,
                    maxLength: 250,
                    textLocalizationKey: 'bm.import.wizard.clients.column.state',
                    translateService: this.translateService,
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.Zip,
                    isMandatory: false,
                    maxLength: 50,
                    textLocalizationKey: 'bm.import.wizard.clients.column.zip',
                    translateService: this.translateService,
                    matcher: new ColumnMatcher({
                        translateService: this.translateService,
                        synonyms: ['bm.import.wizard.clients.column.zip.zip'],
                    }),
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.County,
                    isMandatory: false,
                    maxLength: 250,
                    textLocalizationKey: 'bm.import.wizard.clients.column.county',
                    translateService: this.translateService,
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.PhoneNumber,
                    isMandatory: false,
                    maxLength: 50,
                    textLocalizationKey: 'bm.import.wizard.clients.column.phoneNumber',
                    translateService: this.translateService,
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.Extension,
                    isMandatory: false,
                    maxLength: 50,
                    textLocalizationKey: 'bm.import.wizard.clients.column.extension',
                    translateService: this.translateService,
                    matcher: new ColumnMatcher({
                        translateService: this.translateService,
                        synonyms: ['bm.import.wizard.clients.column.extension.ext'],
                    }),
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new StringColumnOptions({
                    name: ClientColumns.Email,
                    isMandatory: false,
                    maxLength: 250,
                    textLocalizationKey: 'bm.import.wizard.clients.column.email',
                    translateService: this.translateService,
                    matcher: new ColumnMatcher({
                        translateService: this.translateService,
                        synonyms: ['bm.import.wizard.clients.column.email.email'],
                    }),
                    regex: EMAIL_REGEX,
                    regexHowToFixError: this.translateService.instant('bm.import.wizard.validation.email'),
                    // To prevent default format of Wijmo Grid from being applied, when a number is passed.
                    displayOptions: new DisplayOptions(null, 's'),
                }),
                new DateColumnOptions({
                    name: ClientColumns.StartDate,
                    isMandatory: false,
                    locale: this.locale,
                    textLocalizationKey: 'bm.import.wizard.clients.column.startDate',
                    translateService: this.translateService,
                }),
            ];
        });
    }
    /**
     * Creates new regex that matches any of the given options (case-insensitive)
     * @param opts list of options to match. each option can be a string or regex of its own
     * @returns combined regex that accepts any of the given options
     */
    combineRegexes(opts, flags = 'i') {
        const source = opts.map(opt => (typeof opt === 'string' ? opt : `(^${opt.source}$)`)).join('|');
        const regex = new RegExp(source, flags);
        return regex;
    }
    getAllowedEntityTypeNames() {
        return __awaiter(this, void 0, void 0, function* () {
            const entityTypes = yield this.getAllEntityTypes();
            /** Filter out OFFICE entities because they're not valid here */
            return entityTypes.filter(t => t.entityCategory.id !== 'OFFICE').map(t => t.name);
        });
    }
    getAllEntityTypes(force = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const companyId = yield getCompanyId();
            const countryCode = getCountryCode().toUpperCase();
            const cacheId = `${companyId}-${countryCode}`;
            if (cacheId in this.entityTypesCache || force) {
                return this.entityTypesCache[cacheId];
            }
            const items = [];
            let pageIndex = 1;
            let totalPages = Infinity;
            for (let i = pageIndex - 1; i < totalPages; ++i) {
                const { items: _items, pageIndex: _pageIndex, totalPages: _totalPages, } = yield this.getEntityTypes({ companyId, countryCode }, { pageIndex });
                items.push(..._items);
                totalPages = _totalPages;
                pageIndex++;
            }
            this.entityTypesCache[cacheId] = items;
            return items;
        });
    }
    getEntityTypes({ companyId, countryCode = 'US' }, { pageIndex = 1, itemsPerPage = 100 } = {}) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const base = yield getRoute('CoreServices');
            const url = `${base}/v2/companies/${companyId}/contact-entity-types/search`;
            const body = {
                pagingDataRequest: {
                    itemsPerPage,
                    pageIndex,
                },
                contactCountryId: countryCode,
            };
            let respJson;
            try {
                const resp = yield fetch(url, {
                    method: 'POST',
                    headers: jsonHeaders(),
                    body: JSON.stringify(body),
                });
                respJson = yield resp.json();
                /** Handle server errors */
                yield throwOn(resp, s => s >= 400);
                return respJson;
            }
            catch (e) {
                if ((_a = respJson === null || respJson === void 0 ? void 0 : respJson.error) === null || _a === void 0 ? void 0 : _a.message) {
                    console.error(respJson.error.message);
                }
                throw e;
            }
        });
    }
}
ImportClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportClientService, deps: [{ token: LOCALE_ID }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
ImportClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportClientService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportClientService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [LOCALE_ID]
                    }] }, { type: i1.TranslateService }];
    } });

registerLocaleData(localEnGb);
registerLocaleData(localFrCa);
class ImportWizardModule {
    constructor(importWizard, importWip, importLedger, importClient) {
        /**
         * Register all the imports that this module supports
         */
        importWizard.addImport(ImportType.WIP, importWip);
        importWizard.addImport(ImportType.Ledger, importLedger);
        importWizard.addImport(ImportType.Client, importClient);
    }
}
ImportWizardModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportWizardModule, deps: [{ token: ImportWizardService }, { token: ImportWipService }, { token: ImportLedgerService }, { token: ImportClientService }], target: i0.ɵɵFactoryTarget.NgModule });
ImportWizardModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportWizardModule, declarations: [ImportWizardComponent,
        SelectFileComponent,
        SelectFileFromDriveComponent,
        SelectImportSourceComponent,
        ImportMappingComponent,
        ImportGridComponent,
        ModalColumnMappingComponent,
        SelectFileFromDocumentsComponent,
        ImportStatusComponent,
        WipStatusComponent,
        LedgerStatusComponent,
        DrawerIconPipe,
        ImportValidationComponent,
        AlertComponent,
        ClientStatusComponent], imports: [i1.TranslateModule, FormsModule,
        BentoModule,
        NgbModule,
        CommonModule,
        BrowserAnimationsModule,
        WjGridModule,
        DocumentComponentsModule], exports: [ImportWizardComponent] });
ImportWizardModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportWizardModule, providers: [
        {
            provide: LOCALE_ID,
            useFactory: getCurrentLocale,
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            useFactory: getCurrencyCode,
        },
    ], imports: [[
            TranslateModule.forChild(),
            FormsModule,
            BentoModule,
            NgbModule,
            CommonModule,
            BrowserAnimationsModule,
            WjGridModule,
            DocumentComponentsModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportWizardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ImportWizardComponent,
                        SelectFileComponent,
                        SelectFileFromDriveComponent,
                        SelectImportSourceComponent,
                        ImportMappingComponent,
                        ImportGridComponent,
                        ModalColumnMappingComponent,
                        SelectFileFromDocumentsComponent,
                        ImportStatusComponent,
                        WipStatusComponent,
                        LedgerStatusComponent,
                        DrawerIconPipe,
                        ImportValidationComponent,
                        AlertComponent,
                        ClientStatusComponent,
                    ],
                    imports: [
                        TranslateModule.forChild(),
                        FormsModule,
                        BentoModule,
                        NgbModule,
                        CommonModule,
                        BrowserAnimationsModule,
                        WjGridModule,
                        DocumentComponentsModule,
                    ],
                    exports: [ImportWizardComponent],
                    entryComponents: [ImportWizardComponent, ModalColumnMappingComponent, WipStatusComponent, LedgerStatusComponent],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                    providers: [
                        {
                            provide: LOCALE_ID,
                            useFactory: getCurrentLocale,
                        },
                        {
                            provide: DEFAULT_CURRENCY_CODE,
                            useFactory: getCurrencyCode,
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: ImportWizardService }, { type: ImportWipService }, { type: ImportLedgerService }, { type: ImportClientService }]; } });

/*
 * Public API Surface of import-wizard
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ImportWizardComponent, ImportWizardModule };
